<template>
  <div class="pa-4">
    <div v-if="loading" class="text-center">
      <v-skeleton-loader type="list-item-avatar, divider, article, actions"></v-skeleton-loader>
    </div>
    <div v-else v-allowed="'view_purchases'">
      <v-alert type="warning" v-if="isIncompleteCustomAch">
        You have until <b>{{ $d(isIncompleteCustomAch, "month_day_year", $i18n.locale) }}</b> to complete this
        transaction.
        The customer has 24 hours to complete this transaction. After 24 hours, the transaction will automatically
        cancel itself and a new one will need to be sold after. Please use Instant Verification only. Micro-deposits are
        not available at this time. If you have any questions, please contact your account manager.
      </v-alert>
      <v-card class="text-center" ref="print_product" v-if="product && transactionItems" elevation="0" outlined>
        <v-list dense>
          <v-subheader>{{ $t('message.items') }}</v-subheader>
          <template v-for="(i, index) in transactionItems">
            <v-list-item dense :key="'item_' + index" class="text-left">
              <v-list-item-avatar size="30">
                <ImageDisplay v-if="i.product && i.product.image" :src="i.product.image" />
                <ImageDisplay v-else :src="academy.image" />
              </v-list-item-avatar>
              <v-list-item-content v-if="i.product">
                <v-list-item-title>
                  {{ i.product.name }}
                </v-list-item-title>
                <v-list-item-subtitle v-if="i.product.interval">
                  <v-icon size="20" class="mr-1">mdi-autorenew</v-icon>{{ getIntervalDisplay(i.product.interval,
                    i.product.customDaysInterval) }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-if="i.product.size">
                  {{ i.product.size }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-content v-else>
                <v-list-item-title>
                  {{ i.name }}
                </v-list-item-title>
                <v-list-item-subtitle v-if="i.description">
                  {{ i.description }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-chip small v-if="i.amountDecimal > 0">
                  {{ academy.currencySymbol + i.amountDecimal }}
                  <span class="ml-1" v-if="i.quantity > 1">X {{ i.quantity }}</span>
                </v-chip>
                <v-chip small v-else color="secondary">
                  {{ $t('message.free').toUpperCase() }}
                </v-chip>
              </v-list-item-action>
            </v-list-item>
            <v-divider v-if="index + 1 < transactionItems.length"
              :key="'item_transaction_dividier_' + index"></v-divider>
          </template>
        </v-list>
        <div class="text-left pl-2 pb-2">
          <span>TOTAL: {{ academy.currencySymbol }}{{ totalAmount }}</span>
          <span v-if="transaction.coupon">
            <span class="ml-1">-</span>
            <v-icon class="ml-1" size="15" color="accent">mdi-tag-outline</v-icon>
            <span v-if="transaction.coupon.discountPercent">{{ transaction.coupon.discountPercent }}%</span>
            <span class="ml-1" v-else>{{ academy.currencySymbol }}{{ transaction.coupon.discountAmount }}</span>
            <span class="ml-1">=</span>
            <span class="ml-1">{{ academy.currencySymbol }}{{ transaction.coupon.discountedTotal }}</span>
          </span>
        </div>
      </v-card>

      <!-- expired / past-due alert banner -->
      <v-alert v-if="transaction && !transaction.cancelled && (expired || status === 'past_due')" type="error" dense
        class="mt-2 mb-0" :icon="status === 'past_due' ? 'mdi-credit-card-off-outline' : 'mdi-calendar-remove-outline'">
        <div class="d-flex align-center justify-space-between flex-wrap" style="gap:8px;">
          <span>{{ status === 'past_due' ? $t('message.subscription_past_due_alert') :
            $t('message.subscription_expired_alert') }}</span>
          <v-btn v-if="status === 'past_due' && pastDueInvoice" small outlined color="white" :disabled="retryingPayment"
            :loading="retryingPayment" @click="retryPayment(pastDueInvoice)">
            {{ $t('message.retry_now') }}
          </v-btn>
        </div>
      </v-alert>

      <v-card class="mt-2" v-if="academy && transaction" elevation="0" outlined>
        <v-list dense>
          <v-list-item two-line>
            <v-list-item-action>
              <v-icon>mdi-calendar</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ $d(new Date(createdDate), "long", $i18n.locale) }}
                <v-btn v-if="transaction.paidWithCash" small icon @click="transactionDateDialog = true">
                  <v-icon size="20">mdi-calendar-edit-outline</v-icon>
                </v-btn>
              </v-list-item-title>
              <v-list-item-subtitle>{{ $t('message.transaction_date') }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item two-line v-if="transaction.completed">
            <v-list-item-action>
              <v-icon>mdi-calendar-check-outline</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ $d(new Date(transaction.completed), "long", $i18n.locale) }}
              </v-list-item-title>
              <v-list-item-subtitle>{{ $t('message.completed') }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item two-line v-if="transaction.user">
            <v-list-item-action>
              <v-icon>mdi-calendar</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ transaction.user.displayName }}</v-list-item-title>
              <v-list-item-subtitle>{{ $t('message.processed_by') }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-avatar size="30" v-if="transaction.user.userImageSmall">
              <ImageDisplay :src="transaction.user.userImageSmall" />
            </v-list-item-avatar>
          </v-list-item>
          <v-divider v-if="transaction.cancelled"></v-divider>
          <v-list-item two-line v-if="transaction.cancelled">
            <v-list-item-action>
              <v-icon>mdi-calendar-remove-outline</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ $d(new Date(transaction.cancelled), "long", $i18n.locale) }}
              </v-list-item-title>
              <v-list-item-subtitle>{{ $t('message.cancelled_on') }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action v-if="isAppAdmin($store.state.user._id)">
              <v-btn fab x-small @click="unCancelTransaction()">
                <v-icon>mdi-undo</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
          <v-list-item two-line v-if="transaction.cancelledBy">
            <v-list-item-action>
              <v-icon>mdi-account-alert</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ transaction.cancelledBy.displayName }}
              </v-list-item-title>
              <v-list-item-subtitle>{{ $t('message.cancelled_by') }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-avatar size="30" v-if="transaction.cancelledBy.userImageSmall">
              <ImageDisplay :src="transaction.cancelledBy.userImageSmall" />
            </v-list-item-avatar>
          </v-list-item>
          <v-divider v-if="transaction.buyer || (product && product.type != 'service')"></v-divider>
          <v-list-item two-line v-if="transaction.buyer" @click="checkMemberAndGo()">
            <v-list-item-action>
              <v-icon>mdi-account-outline</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                {{ transaction.buyer.displayName }}
                <v-btn small icon @click.stop="assignBuyerDialog = true">
                  <v-icon size="20">mdi-pencil-box-outline</v-icon>
                </v-btn>
              </v-list-item-title>
              <v-list-item-subtitle>{{ $t('message.sold_to') }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-avatar size="30" v-if="transaction.buyer.userImageSmall">
              <ImageDisplay :src="transaction.buyer.userImageSmall" />
            </v-list-item-avatar>
          </v-list-item>
          <v-list-item two-line v-else-if="product && product.type != 'service'" @click="checkMemberAndGo()">
            <v-list-item-action>
              <v-icon>mdi-account-outline</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                <v-btn small text @click="assignBuyerDialog = true">
                  {{ $t('message.assign_buyer') }}
                </v-btn>
              </v-list-item-title>
              <v-list-item-subtitle>{{ $t('message.sold_to') }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item two-line>
            <v-list-item-action>
              <v-icon>mdi-at</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ transaction.email }}</v-list-item-title>
              <v-list-item-subtitle>Email</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item two-line v-if="product && product.type == 'good'">
            <v-list-item-action>
              <v-icon>mdi-checkbox-multiple-blank-outline</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ transaction.quantity }}</v-list-item-title>
              <v-list-item-subtitle>{{ $t('message.quantity') }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-divider v-if="product && product.type == 'good'"></v-divider>
          <v-list-item two-line>
            <v-list-item-action>
              <v-icon size="20" v-if="updating" class="rotate">mdi-refresh</v-icon>
              <TransactionIcon size="20" v-else :transaction="transaction"
                :status-color="getStatusColor(transaction, status)">
              </TransactionIcon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                <span>{{ isCustomAchTransaction(transaction) && !transaction.completed ? $t("message.pending") :
                  getPaymentStatus() }}</span>
                <v-btn small icon v-if="!transaction.cancelled && transaction.completed && status == 'trialing'">
                  <v-icon @click="editTrialEndDateDialog = true" size="20">mdi-pencil-box-outline</v-icon>
                </v-btn>
                <span class="error--text ml-2" v-if="transaction.expired">{{
                  $t("message.expired") }}</span>
              </v-list-item-title>
              <v-list-item-subtitle>{{ $t('message.status') }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action v-if="transaction.status == 'pending_payment'">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" :loading="sendingReminder" icon @click="sendReminder()">
                    <v-icon>mdi-email-fast-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('message.send_reminder') }}</span>
              </v-tooltip>
            </v-list-item-action>
          </v-list-item>
          <v-list-item dense v-if="!paymentsReady">
            <v-list-item-action>
              <PaymentType size="20" :type="transaction.paidWith"></PaymentType>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                {{ transaction.paidWith ? $t("message." + transaction.paidWith) : $t("message.cash") }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ $t("message.paid_with") }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn small icon @click="editPaidWithDialog = true"><v-icon>mdi-square-edit-outline</v-icon></v-btn>
            </v-list-item-action>
          </v-list-item>
          <v-list-item two-line v-if="!transaction.cancelled && transaction.cancellationDate">
            <v-list-item-action>
              <v-icon size="20" color="error">mdi-timer-check-outline</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                {{ $d(new Date(transaction.cancellationDate), "month_day_year", $i18n.locale) }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ $t('message.cancellation_date') }}
                <span class="ml-1" v-if="transaction.cancelAndRemoveMember">+</span>
                <v-icon size="20" v-if="transaction.cancelAndRemoveMember"
                  class="ml-1">mdi-account-cancel-outline</v-icon>
                <span class="ml-1" v-if="transaction.cancelAndRemoveMember && !isSmallerDisplay()">{{
                  $t('message.user_will_be_removed_from_academy') }}</span>
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-bind="attrs" v-on="on" small icon class="ml-1" @click="processScheduleCancellation(true)">
                    <v-icon>mdi-timer-remove-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('message.remove_scheduled_cancellation') }}</span>
              </v-tooltip>
            </v-list-item-action>
          </v-list-item>
          <v-divider v-if="transaction.cancellationDate"></v-divider>
          <v-list-item two-line v-if="transaction.subscriptionStartDate">
            <v-list-item-action>
              <v-icon>mdi-calendar-arrow-right</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                <span :class="'text-body-2' + (!transaction.completed && tooCloseToStartDate ? ' error--text' : '')">
                  {{ $d(new Date(transaction.subscriptionStartDate), "month_day_year", $i18n.locale) }}
                </span>
                <span class="ml-2 error--text text-body-2" v-if="!transaction.completed && tooCloseToStartDate">
                  ({{ $t("message.at_least_2_days") }})
                </span>
              </v-list-item-title>
              <v-list-item-subtitle>{{ $t('message.subscription_start_date') }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action class="ml-2 error--text text-body-2"
              v-if="!transaction.completed && !transaction.cancelled && transaction.subscriptionStartDate">
              <v-btn @click="editTrialEndDateDialog = true" small icon>
                <v-icon size="15">mdi-pencil-box-outline
                </v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item two-line v-if="product && product.interval">
            <v-list-item-action>
              <v-icon v-if="product.interval == 'once'">mdi-numeric-1-box-outline</v-icon>
              <v-icon v-else size="20">mdi-refresh</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                <span class="text-body-2">
                  {{ getIntervalDisplay(product.interval, product.customDaysInterval) }}
                </span>
              </v-list-item-title>
              <v-list-item-subtitle>{{ $t('message.interval') }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-divider v-if="product && product.interval"></v-divider>
          <v-list-item two-line
            v-if="(transaction.billingDate || (transaction.updateBillingDate && transaction.updateBillingDate.date)) && product && product.interval == 'month' && transaction.completed && !transaction.cancelled">
            <v-list-item-action>
              <v-icon>mdi-calendar-cursor-outline</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                <span class="text-body-2" v-if="transaction.updateBillingDate">
                  {{ $t('message.of_every_month', [new Date(transaction.updateBillingDate.date).getDate()]) }}
                </span>
                <span class="text-body-2" v-else>
                  {{ $t('message.of_every_month', [new Date(transaction.billingDate).getDate()]) }}
                </span>
              </v-list-item-title>
              <v-list-item-subtitle>{{ $t('message.updated_billing_date') }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action v-if="product">
              <v-btn v-if="product.interval == 'month' && transaction.completed && !transaction.cancelled" small icon
                @click="billingDateDialog = true">
                <v-icon>mdi-pencil-box-outline</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
          <v-divider
            v-if="transaction.billingDate && product && product.interval == 'month' && transaction.completed && !transaction.cancelled"></v-divider>
          <v-list-item two-line
            v-if="transaction.coupon && (transaction.coupon.discountPercent || transaction.coupon.discountAmount)">
            <v-list-item-action>
              <v-icon>mdi-tag-outline</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                <span v-if="transaction.coupon.discountPercent">
                  {{ transaction.coupon.discountPercent }}%
                </span>
                <span v-if="transaction.coupon.discountAmount">
                  {{ academy.currencySymbol }}{{ transaction.coupon.discountAmount }}
                </span>
                <span class="ml-1" v-if="transaction.coupon.expirationDate">({{ $t('message.expires') }} {{ $d(new
                  Date(transaction.coupon.expirationDate),
                  'month_day_year', $i18n.locale) }})</span>
              </v-list-item-title>
              <v-list-item-subtitle>{{ $t('message.discount') }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn small icon @click="updateCouponDialog = true">
                <v-icon>mdi-tag-off-outline</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
          <v-list-item two-line v-else-if="product && product.interval != 'once' && transaction.completed">
            <v-list-item-action>
              <v-icon>mdi-tag-outline</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>
                {{ $t('message.no_discount') }}
              </v-list-item-title>
              <v-list-item-subtitle>{{ $t('message.discount') }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn icon small @click="updateCouponDialog = true">
                <v-icon size="20">mdi-tag-plus-outline</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
          <v-divider></v-divider>
          <v-list-item two-line v-if="transaction && transaction.documentTemplate">
            <v-list-item-action>
              <v-icon>mdi-draw-pen</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title> {{ transaction.documentTemplate.name }}</v-list-item-title>
              <v-list-item-subtitle>{{ $t('message.contract') }}</v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn color="success" v-if="signedContract" small icon @click="goToSignedDocument()">
                <v-icon>mdi-file-document-check-outline</v-icon>
              </v-btn>
              <v-tooltip top v-else>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon color="error" v-bind="attrs" v-on="on">mdi-file-document-alert-outline</v-icon>
                </template>
                <span>{{ $t('message.pending') }}</span>
              </v-tooltip>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <v-divider v-if="transaction && transaction.documentTemplate"></v-divider>
        <v-sheet v-if="paymentMethod" :rounded="isSmallerDisplay() ? '' : 'lg'" class="mt-2 pa-2">
          <div class="text-subtitle-1 font-weight-bold mr-2 text-left ml-2">{{ $t("message.payment_method") }}:</div>
          <div>
            <PaymentMethod class="card-size" :height="60" :payment-method="paymentMethod" :hide-name="true">
            </PaymentMethod>
          </div>
          <div v-if="paymentMethod.differentOwner">
            <v-list-item two-line
              @click="goToMember(paymentMethod.differentOwner._id, paymentMethod.differentOwner.displayName)">
              <v-list-item-avatar v-if="paymentMethod.differentOwner.userImageSmall">
                <ImageDisplay :src="paymentMethod.differentOwner.userImageSmall" />
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title> {{ paymentMethod.differentOwner.displayName }}</v-list-item-title>
                <v-list-item-subtitle>{{ $t('message.different_owner') }}</v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon color="error">mdi-alert</v-icon>
              </v-list-item-action>
            </v-list-item>
            <v-alert type="warning">
              {{ $t('message.payment_method_different_owner_explained') }}
            </v-alert>
          </div>
        </v-sheet>
        <v-card-title v-if="transaction.prorated && transaction.prorated.amount">
          <div class="text-subtitle-1 font-weight-bold mr-2">{{ $t("message.prorated_amount") }}:</div>
          <span class="gray--text text-body-2">{{ academy.currencySymbol }}{{ transaction.prorated.amount }}</span>
          <span class="gray--text ml-1 mr-1 text-body-2">-</span>
          <span class="gray--text text-body-2">{{ transaction.prorated.days }} {{ $t("message.days") }}</span>
        </v-card-title>
        <v-card-text style="font-size: 1.5em" class="text-left">
          <div>
            <span class="text-h6 mr-1">Total:</span>
            <span v-if="transaction.coupon && transaction.coupon.discountedTotal >= 0" class="text-h6">
              {{ academy.currencySymbol }}{{ transaction.coupon.discountedTotal }}
            </span>
            <span v-else class="text-h6">
              {{ academy.currencySymbol }}{{ totalAmount * transaction.quantity }}
            </span>
          </div>
          <div class="text-caption" v-if="!isNaN(transaction.taxAmount)">
            <span class="mr-1 ml-1">{{ $t("message.tax") }}:</span>
            <span>{{ academy.currencySymbol }}{{ transaction.taxAmount }}</span>
          </div>
          <div class="text-caption" v-else-if="checkoutSession && checkoutSession.automaticTax">
            <span class="mr-1 ml-1">{{ $t("message.tax") }}:</span>
            <span>{{ academy.currencySymbol }}{{ centsToAmount(checkoutSession.totalDetails.amount_tax) }}</span>
          </div>
        </v-card-text>
        <v-card-text v-if="transaction.refunds && transaction.refunds.length > 0" class="text-left error--text">
          <div class="text-subtitle-1 font-weight-bold mr-2">{{ $t("message.refunds") }}</div>
          <div v-for="(r, index) in transaction.refunds" :key="'refund_' + index">{{ index + 1 }} - {{ $d(new
            Date(r.date),
            "long", $i18n.locale) }}: {{ r.user.displayName }} - {{ r.amount }} {{ currency }}</div>
        </v-card-text>
      </v-card>
      <v-card elevation="0" outlined class="mt-2"
        v-if="!transaction.cancelled && product && product.type == 'service' && pausedUntil">
        <v-card-text>
          <div v-if="transaction.pauseDate">
            <v-icon color="yellow">mdi-calendar-clock</v-icon>
            {{ $t("message.pause_starts") }}:
            {{ $d(new Date(transaction.pauseDate), "month_day_year", $i18n.locale) }}
          </div>
          <div>
            <v-icon color="yellow">mdi-pause-circle-outline</v-icon>
            {{ $t("message.paused_until") }}:
            {{ $d(new Date(pausedUntil), "month_day_year", $i18n.locale) }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined small elevation="0" color="primary" @click="resumeSubscriptionDialog = true">
            <v-icon>mdi-play-circle-outline</v-icon>
            {{ $t("message.resume") }}
          </v-btn>
        </v-card-actions>
      </v-card>
      <v-card class="mt-2" v-if="product && product.type == 'good' && transaction.buyer" elevation="0" outlined>
        <v-card-text>
          <v-switch hide-details dense v-model="isSubscription" :label="isSubscription
            ? $t('message.counts_as_subscription')
            : $t('message.count_as_subscription')
            "></v-switch>
          <v-switch hide-details @change="updateManualRecurrence()" dense v-model="transaction.manualRecurrence"
            :label="$t('message.keep_membership_visible')"></v-switch>
          <div class="text-caption mb-2">{{ $t('message.manual_recurrence_explained') }}</div>
          <div v-if="transaction.subscriptionEndDate" class="text-left mt-1">
            <b class="error--text" v-if="expired">{{ $t("message.expired") }}:</b>
            <b v-else>{{ $t("message.expires") }}:</b>
            {{ $d(new Date(transaction.subscriptionEndDate), "month_day_year", $i18n.locale) }}
            <v-btn small icon @click="editSubscriptionEndDate = !editSubscriptionEndDate">
              <v-icon size="15">mdi-calendar-edit-outline</v-icon>
            </v-btn>
          </div>
          <v-card outlined
            v-if="(!transaction.subscriptionEndDate && !transaction.subscriptionCheckins && isSubscription) || editSubscriptionEndDate">
            <v-card-actions class="mb-2">
              <DatePicker :no-boundries="true" v-model="subscriptionEndDate">
              </DatePicker>
              <v-spacer></v-spacer>
              <v-btn outlined elevation="0" color="primary" small v-if="subscriptionEndDate"
                @click="addSubscriptionEndDate()">
                {{ $t("message.update") }}
              </v-btn>
            </v-card-actions>
          </v-card>
          <div v-if="transaction.subscriptionCheckins" class="text-left" style="margin-left:10px;margin-top:5px">
            <div>
              <v-icon>mdi-map-marker-outline</v-icon>
              <b>{{ $t("message.subscription_checkins") }}:</b>
              <span class="ml-2" v-if="totalCheckins >= 0">{{ totalCheckins ? totalCheckins : 0 }}/</span>
              <span>{{ transaction.subscriptionCheckins }}</span>
              <v-btn icon @click="editSubscriptionCheckinsDialog = true" class="ml-2 mb-1"
                x-small><v-icon>mdi-pencil-circle-outline</v-icon></v-btn>
            </div>
            <div v-if="transaction.expired" class="text-caption error--text">{{ $t("message.expired") }}</div>
          </div>
        </v-card-text>
      </v-card>
      <v-card class="mt-2" v-if="transaction.paidWithCash" elevation="0" outlined>
        <v-card-actions>
          <v-btn v-if="transaction.subscriptionEndDate || transaction.subscriptionCheckins" elevation="0" small outlined color="primary"
            @click="openRenewDialog()">
            <v-icon>mdi-chevron-double-up</v-icon>
            {{ $t("message.renew_membership") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn v-if="false" elevation="0" small color="error" @click="cancelCashTransactionDialog = true">
            <v-icon>mdi-trash-can-outline</v-icon>
            {{ $t("message.remove") }}
          </v-btn>
        </v-card-actions>
      </v-card>
      <v-expansion-panels flat class="mt-2" v-if="transaction">
        <v-expansion-panel v-if="isSubscription"
          :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'">
          <v-expansion-panel-header class="text-left ml-1">
            <div>
              <v-icon class="mr-1"
                :color="(transaction.allowedClasses && transaction.allowedClasses.length > 0) || (transaction.allowedCustomClasses && transaction.allowedCustomClasses.length > 0) || (allowedClassCount && allowedClassCount.interval) ? 'accent' : ''">mdi-book-lock-outline</v-icon>
              {{ $t("message.restrict_attendance") }}
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pa-0">
            <div class="text-caption">
              {{ $t('message.restrict_attendance_transaction_explained') }}
            </div>
            <v-subheader>{{ $t('message.by_class_name') }}</v-subheader>
            <div>
              <AvailableClasses :label="$t('message.allowed_classes')" :vm="this"
                :update-function="updateAllowedClasses" v-model="transaction.allowedClasses"></AvailableClasses>
              <v-combobox class="mt-2" v-if="transaction.allowedCustomClasses"
                v-model="transaction.allowedCustomClasses" :items="transaction.allowedCustomClasses" small-chips
                outlined hide-details dense @change="updateAllowedClassesPost(this, 'allowedCustomClasses')"
                :label="$t('message.custom_names')" :multiple="true" clearable>
              </v-combobox>
            </div>
            <v-subheader>{{ $t('message.by_class_count') }}</v-subheader>
            <div class="mt-1">
              <v-select @change="updateAllowedClassesPost(null, 'count')" outlined dense :items="allowedClassIntervals"
                v-model="allowedClassCount.interval" :label="$t('message.class_count_interval')"
                :clearable="true"></v-select>
            </div>
            <div v-if="allowedClassCount.interval">
              <v-text-field @change="updateAllowedClassesPost(null, 'count')" hide-details outlined dense
                v-model="allowedClassCount.count" number :label="$t('message.allowed_classes')"></v-text-field>
              <v-switch hide-details @change="updateAllowedClassesPost(null, 'count')"
                v-model="allowedClassCount.sendEmail" :label="$t('message.send_email')"></v-switch>
              <div class="text-caption ml-1 gray--text">
                {{ $t('message.allowed_class_count_send_email_explained') }}
              </div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel v-if="isSubscription"
          :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'">
          <v-expansion-panel-header class="text-left ml-1">
            <div>
              <v-icon class="mr-1"
                :color="transaction.tags && transaction.tags.length > 0 ? 'accent' : ''">mdi-label-outline</v-icon>
              {{ $t("message.tags") }}
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pa-0">
            <TagsCombo @input="updateTags" v-model="transaction.tags" :label="''"></TagsCombo>

          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel v-if="isMembership" :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'">
          <v-expansion-panel-header class="text-left ml-1">
            <div>
              <v-icon class="mr-1"
                :color="(transaction.altMembers && transaction.altMembers.length > 0) || (transaction.altMembersCount && transaction.altMembersCount > 0) ? 'accent' : ''">mdi-account-group-outline</v-icon>
              {{ $t("message.extra_members") }}
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pa-0">
            <v-card-text class="pa-0" v-if="!transaction.altMembersCount || transaction.altMembersCount == 0">
              <MembersAutocomplete v-on:user_picked="updateAltMembers" v-model="newAltMember"
                :label="$t('message.pick_member')">
              </MembersAutocomplete>
              <div class="text-caption">
                {{ $t("message.alt_members_explained") }}
              </div>
            </v-card-text>
            <v-card-text style="margin-top: -45px">
              <v-list>
                <v-list-item two-line v-for="u in transaction.altMembers" :key="'altmember_' + u._id"
                  @click="goToMember(u._id, u.displayName)">
                  <v-list-item-avatar v-if="u.userImageSmall">
                    <ImageDisplay :src="u.userImageSmall" />
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title>{{ u.displayName }}</v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn @click.stop="updateAltMembers(u._id, 'remove')" small icon>
                      <v-icon>mdi-close-circle-outline</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card-text>
            <div v-if="!transaction.altMembers || !transaction.altMembers.length">
              <v-text-field @focusout="updateAltMembersCount()" hide-details outlined dense
                :disabled="transaction.altMembers && transaction.altMembers.length ? true : false"
                v-model="transaction.altMembersCount" :label="$t('message.extra_members_quantity')"
                :rules="[validationRules.number]"></v-text-field>
              <div class="text-caption">{{ $t('message.extra_members_quantity_explained') }}</div>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel v-if="history && history.length > 0"
          :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'">
          <v-expansion-panel-header class="text-left ml-1">
            <div>
              <v-icon color="accent" class="mr-1">mdi-clock-outline</v-icon>
              {{ $t("message.history") }}
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <AcademyActivity :activity="history"></AcademyActivity>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel v-if="isSubscription && invoices && product && product.type == 'service'"
          :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'">
          <v-expansion-panel-header @click="getInvoices(true)">
            <div class="text-left ml-1">
              <v-icon class="mr-1"
                :color="invoices && invoices.length > 0 ? 'accent' : ''">mdi-text-box-outline</v-icon>
              {{ $t("message.invoices") }}
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-skeleton-loader v-if="loadingInvoices"
              type="list-item-avatar, divider, list-item-avatar"></v-skeleton-loader>
            <v-list-item disabled v-if="scheduledBillingDateUpdate">
              <v-list-item-action>
                <v-icon size="20">mdi-calendar-clock</v-icon>
              </v-list-item-action>
              <v-list-item-content class="text-left" v-if="nextBillingDate.amount">
                <v-list-item-title>
                  {{ $t("message.scheduled_billing_date_update") }}:
                  {{ $d(new Date(scheduledBillingDateUpdate.date), "month_day_year", $i18n.locale) }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span v-if="nextBillingDate.hasProration" class="secondary--text text-caption ml-2 mb-1">{{
                    $t('message.includes_prorated_amount') }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-content class="text-left" v-else>
                <v-list-item-title>{{ $t("message.next_invoice") }}:
                  {{ $d(new Date(nextBillingDate), "short", $i18n.locale) }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item disabled v-if="nextBillingDate">
              <v-list-item-action>
                <v-icon v-if="nextBillingDate.cancelled" size="20">mdi-cash-off</v-icon>
                <v-icon v-else size="20">mdi-sync</v-icon>
              </v-list-item-action>
              <v-list-item-content class="text-left" v-if="nextBillingDate.amount">
                <v-list-item-title>
                  {{ $t("message.next_invoice") }}:
                  {{ $d(new Date(nextBillingDate.date), "month_day_year", $i18n.locale) }}
                  <span class="ml-1" v-if="nextBillingDate.cancelled">({{ $t("message.cancelled") }})</span>
                </v-list-item-title>
                <v-list-item-subtitle v-if="nextBillingDate.amount">
                  {{ academy.currencySymbol }}{{ nextBillingDate.amount }}
                </v-list-item-subtitle>
                <v-list-item-subtitle v-if="nextBillingDate.futureUpdate">
                  <span v-if="nextBillingDate.skipProration" class="secondary--text text-caption mb-1">{{
                    $t('message.updated_billing_date') }} - {{
                      $t('message.full_amount') }}</span>
                  <span v-else class="secondary--text text-caption mb-1">{{ $t('message.updated_billing_date') }} -
                    {{
                      $t('message.prorated_amount') }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-content class="text-left" v-else>
                <v-list-item-title>{{ $t("message.next_invoice") }}:
                  {{ $d(new Date(nextBillingDate), "short", $i18n.locale) }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <template v-for="(i, index) in invoices">
              <v-list-item :key="'invoice_' + index"
                :style="targetInvoice && targetInvoice.id == i.id ? 'border:1px solid #FFD700; border-radius: 5px' : ''">
                <v-list-item-action>
                  <v-icon v-if="i.disputed" color="error">mdi-alert-octagram-outline</v-icon>
                  <v-icon
                    v-else-if="i.paymentIntentStatus && i.paymentIntentStatus == 'canceled'">mdi-close-circle-outline</v-icon>
                  <v-icon v-else-if="i.status == 'voided'">mdi-cash-off</v-icon>
                  <v-icon v-else-if="i.status == 'future_payment'">mdi-cash-clock</v-icon>
                  <v-icon v-else-if="i.amountPaid && i.paidWithCash" color="success">mdi-cash-check</v-icon>
                  <v-icon v-else-if="i.amountPaid" color="success">mdi-check-circle-outline</v-icon>
                  <v-icon v-else-if="i.amountPaid == 0 && i.status == 'paid'"
                    color="success">mdi-check-circle-outline</v-icon>
                  <v-icon v-else-if="i.status == 'completed'" color="success">mdi-check-circle-outline</v-icon>
                  <v-icon v-else-if="i.attempts == 0 && i.status == 'uncollectible'"
                    color="gold">mdi-pause-circle-outline</v-icon>
                  <v-icon v-else-if="i.status == 'paused'" color="gold">mdi-pause-circle-outline</v-icon>
                  <v-icon v-else-if="i.amountDue" color="error">mdi-cash-clock</v-icon>
                </v-list-item-action>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-list-item-content class="text-left" v-bind="attrs" v-on="on">
                      <v-list-item-title>
                        <span class="font-weight-black" v-if="i.amountPaid">{{ academy.currencySymbol }}{{ i.amountPaid
                        }}</span>
                        <span class="font-weight-black" v-else-if="i.attempts == 0">{{ academy.currencySymbol }}{{
                          i.amountDue }}</span>
                        <span class="font-weight-black" v-else-if="i.amountDue">{{ academy.currencySymbol }}{{
                          i.amountDue }}</span>
                        <span class="font-weight-black">:</span>
                        <span class="ml-1" v-if="i.status == 'future_payment' && i.amountPaid == 0">{{
                          $t("message.next_invoice")
                        }}</span>
                        <span v-else-if="i.status == 'paid' && i.amountPaid == 0">{{
                          $t("message.completed") }}</span>
                        <span v-else> {{ i.status }}</span>
                        <span v-if="i.refundedAmount > 0" class="error--text">
                          {{ getCurrencySymbol(currency) + i.refundedAmount }}
                          {{ $t("message.refunded") }}
                        </span>
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        <span v-if="i.created && isInfinicept">
                          {{
                            $d(unixString2Datetime(i.created), "month_day_year", $i18n.locale)
                          }}
                        </span>
                        <span v-else-if="i.created">
                          {{
                            $d(unixString2Datetime(i.created), "long", $i18n.locale)
                          }}
                        </span>
                        <span v-else-if="i.dueDate">
                          {{
                            $d(new Date(i.dueDate), "long", $i18n.locale)
                          }}
                        </span>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                  <span>
                    <span v-if="i.disputed">{{ $t("message.disputed") }}</span>
                    <span v-else>{{ i.errorMessage ? i.errorMessage : getStatusExplained(i.status.toLowerCase())
                    }}</span>
                  </span>
                </v-tooltip>
                <v-list-item-content class="text-left" v-if="!isSmallerDisplay()">
                  <div>
                    <div>
                      {{ $t("message.attempts") }}: {{ i.attempts }}
                      <v-tooltip top v-if="i.attempts == 4">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon v-bind="attrs" v-on="on" size="15" class="ml-1 mr-1"
                            color="error">mdi-alert-outline</v-icon>
                        </template>
                        <span>{{ $t('message.no_more_attempts') }}</span>
                      </v-tooltip>
                    </div>
                    <div v-if="i.nextPaymentAttempt">
                      <span class="grey--text text-caption">
                        {{ $t("message.next_try") }}:
                        {{
                          $d(
                            unixString2Datetime(i.nextPaymentAttempt),
                            "short",
                            $i18n.locale
                          )
                        }}
                      </span>
                    </div>
                  </div>
                </v-list-item-content>
                <v-list-item-action class="text-left">
                  <v-menu offset-y v-if="i.amountPaid > 0">
                    <template v-slot:activator="{ on }">
                      <v-btn elevation="0" v-on="on" icon small slot="activator" left>
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item :disabled="i.refundedAmount >= i.amountPaid"
                        @click.stop="i.refundedAmount < i.amountPaid && showRefundDialog(i)">
                        <v-list-item-title>
                          {{ $t('message.refund') }}
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item v-if="transaction.paymentsModule == 'easypay'"
                        @click="sendEmailDialog = true; selectedInvoice = i;">
                        <v-list-item-title>
                          {{ $t('message.email_receipt') }}
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item v-else-if="i.refundedAmount != i.amountPaid"
                        @click="sendEmailDialog = true; selectedInvoice = i.paymentIntent;">
                        <v-list-item-title>
                          {{ $t('message.email_receipt') }}
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                  <v-menu offset-y v-else-if="canCancelInvoice(i)">
                    <template v-slot:activator="{ on }">
                      <v-btn elevation="0" v-on="on" icon small slot="activator" left>
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list>
                      <v-list-item :disabled="retryingPayment" v-if="i.amountPaid == 0 && i.attempts >= 1"
                        @click.stop="retryPayment(i)">
                        <v-list-item-title>
                          {{ $t('message.retry_now') }}
                          <v-progress-circular v-if="retryingPayment" class="ml-1" size="20" indeterminate
                            color="primary"></v-progress-circular>
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item @click.stop="showCancelPaymentIntentDialog(i)">
                        <v-list-item-title>
                          {{ $t('message.void_invoice') }}
                        </v-list-item-title>
                      </v-list-item>
                      <v-list-item v-allowed="'issue_refund'" v-if="!i.refunded"
                        @click.stop="showCancelPaymentIntentDialog(i, true)">
                        <v-list-item-title>
                          {{ $t('message.paid_with_cash') }}
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-if="index + 1 < invoices.length" :key="'invoice_divider_' + index"></v-divider>
            </template>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'">
          <v-expansion-panel-header>
            <div class="text-left ml-1">
              <v-icon :color="receipt ? 'accent' : ''">mdi-receipt-text</v-icon>
              {{ $t("message.upload_receipt") }}
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card-actions>
              <v-btn v-if="!uploadedReceipt" :disabled="uploading" outlined class="elevation-0" @click="filePicker()"
                color="primary" small>
                <v-icon>mdi-receipt-text-outline</v-icon>
                {{ $t("message.upload_receipt") }}
              </v-btn>
              <v-btn v-else :disabled="uploading" outlined class="elevation-0" @click="updateTransactionReceipt()"
                color="primary" small>
                {{ $t("message.update") }}
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn v-if="receipt" class="elevation-0" @click="receipt = null;" color="error" small dark>
                <v-icon>mdi-trash-can-outline</v-icon>
                {{ $t("message.delete") }}
              </v-btn>
            </v-card-actions>
            <v-progress-linear v-if="uploading" indeterminate color="secondary"></v-progress-linear>
            <v-card-text v-if="receipt">
              <v-responsive>
                <ImageDisplay :src="receipt" />
              </v-responsive>
            </v-card-text>
            <v-card-text>
              <div v-if="uploadingMessage">
                {{ uploadingMessage }}
              </div>
              <input style="display: none" type="file" accept="image/*" @change="onFileSelect" ref="fileUploadInput" />
            </v-card-text>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'">
          <v-expansion-panel-header>
            <div class="text-left ml-1">
              <v-icon
                :color="transaction.notes && transaction.notes != '' ? 'accent' : ''">mdi-message-text-outline</v-icon>
              <span class="ml-1">{{ $t("message.notes") }}</span>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-textarea filled :placeholder="$t('message.type_to_edit')" v-model="transaction.notes"></v-textarea>
            <v-card-actions>
              <v-btn elevation="0" small outlined color="primary" @click="updateTransactionNotes()">
                {{ $t("message.update") }}
              </v-btn>
            </v-card-actions>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-card class="mt-2" elevation="0" v-if="product">
        <v-card-text class="mb-0 pb-0">
          <v-alert type="info" v-if="pendingUpdateCompletion">
            {{ $t('message.finish_payment_in_progress_explained') }}
          </v-alert>
        </v-card-text>
        <v-card-actions v-allowed="'edit_subscription'" v-if="!isSmallerDisplay()">
          <v-btn v-if="onboarding && !transaction.cancelled" elevation="0" small outlined color="primary"
            @click="resumePublicMembershipCheckout()">
            <v-icon>mdi-credit-card-check-outline</v-icon>
            {{ $t("message.complete_onboarding") }}
          </v-btn>
          <v-btn :loading="handlingCharges || processingPayment" outlined elevation="0" small
            v-if="canCompletePayment() && !pendingUpdateCompletion" @click="completePayment()" color="primary">
            <v-icon>mdi-credit-card-check-outline</v-icon>
            {{ $t("message.complete_payment") }}
          </v-btn>
          <v-btn :loading="handlingCharges || processingPayment" outlined elevation="0" small
            v-if="canCompletePayment() && pendingUpdateCompletion" @click="completeUpdate(pendingUpdateCompletion)"
            color="primary">
            <v-icon>mdi-credit-card-check-outline</v-icon>
            {{ $t("message.finish_payment_in_progress") }}
          </v-btn>
          <v-btn outlined small elevation="0" color="primary" :disabled="processingPayment"
            v-if="canCompletePayment(true)" @click="completePaymentCash()">
            <v-icon v-if="!processingPayment">mdi-cash-check</v-icon>
            <v-icon class="rotate" v-else>mdi-refresh</v-icon>
            {{ $t('message.pay_cash') }}
          </v-btn>
          <v-btn outlined elevation="0" color="primary" small @click="updateCardDialog = true" v-if="
            transaction.subscription &&
            !transaction.cancelled && transaction.completed">
            <v-icon v-if="paymentMethod && paymentMethod.type == 'card'">mdi-credit-card-refresh-outline</v-icon>
            <v-icon v-else-if="paymentMethod && paymentMethod.type.indexOf('bank') >= 0">mdi-bank</v-icon>
            <span v-if="paymentMethod && paymentMethod.type == 'card'">{{ $t("message.update_card") }}</span>
            <span v-else>{{ $t("message.update_payment_method") }}</span>
          </v-btn>
          <v-btn outlined elevation="0" color="primary" small
            @click="upgradeMembershipDialog = true; getAvailableMemberships()" v-if="
              transaction.subscription &&
              !transaction.cancelled && transaction.completed && !pausedUntil
            ">
            <v-icon>mdi-autorenew</v-icon>
            {{ $t("message.update_membership") }}
          </v-btn>
          <v-btn outlined elevation="0" color="primary" small @click="pauseSubscriptionDialog = true" v-if="canPause()">
            <v-icon>mdi-pause-circle-outline</v-icon>
            {{ $t("message.pause") }}
          </v-btn>
          <v-btn outlined elevation="0" color="primary" small @click="initUpdateItemsDialog()"
            v-if="canUpdateSubscriptionItems()">
            <v-icon>mdi-circle-multiple-outline</v-icon>
            {{ $t("message.update_items") }}
          </v-btn>
          <v-btn
            v-if="product && product.type != 'service' && transaction.completed && !transaction.paidWithCash && selectedInvoice && totalAmount > totalRefunded"
            elevation="0" small outlined color="primary" @click="showRefundDialog()">
            <v-icon>mdi-credit-card-refund-outline</v-icon>
            {{ $t("message.refund") }}
          </v-btn>
          <v-btn elevation="0" small :disabled="isRestarting"
            v-if="transaction.completed && transaction.cancelled && isMembership && transaction.subscription && isMember"
            @click="restartSubscriptionDialog = true" color="primary" outlined>
            <v-icon :class="isRestarting ? 'rotate' : ''">mdi-restart</v-icon>
            {{ $t("message.restart_subscription") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn small elevation="0" @click="cancelSubscriptionDialog = true" v-if="
            (transaction.subscription || (product && product.type == 'service')) &&
            !transaction.cancelled && status !== 'paused'
          ">
            <v-icon>mdi-delete-circle-outline</v-icon>
            {{ $t("message.cancel") }}
          </v-btn>
          <v-btn small elevation="0" @click="cancelOneTimePayment()" v-else-if="
            product && product.type == 'good' &&
            !transaction.cancelled
          ">
            <v-icon>mdi-delete-circle-outline</v-icon>
            {{ $t("message.cancel") }}
          </v-btn>
          <v-btn small elevation="0" outlined @click="cancelSubscriptionDialog = true" v-else-if="isAppAdmin($store.state.user._id) &&
            transaction.subscription && !transaction.cancelled && status != 'canceled' && status !== 'paused'">
            <v-icon>mdi-delete-circle-outline</v-icon>
            {{ $t("message.cancel") }}
          </v-btn>
        </v-card-actions>
        <v-alert v-if="status === 'paused' && !transaction.cancelled" dense text type="warning" class="mx-3 mb-2">
          {{ $t("message.paused_membership_cannot_be_cancelled") }}
        </v-alert>
        <div v-if="isSmallerDisplay()">
          <v-btn :loading="handlingCharges || processingPayment" outlined class="mb-1" elevation="0" block small
            v-if="canCompletePayment() && !pendingUpdateCompletion" @click="completePayment()" color="primary">
            <v-icon>mdi-credit-card-check-outline</v-icon>
            {{ $t("message.complete_payment") }}
          </v-btn>
          <v-btn :loading="handlingCharges || processingPayment" outlined class="mb-1" elevation="0" block small
            v-if="canCompletePayment() && pendingUpdateCompletion" @click="completeUpdate(pendingUpdateCompletion)"
            color="primary">
            <v-icon>mdi-credit-card-check-outline</v-icon>
            {{ $t("message.finish_payment_in_progress") }}
          </v-btn>
          <v-btn outlined class="mb-1" block small elevation="0" color="primary" :disabled="processingPayment"
            v-if="canCompletePayment(true)" @click="completePaymentCash()">
            <v-icon v-if="!processingPayment">mdi-cash-check</v-icon>
            <v-icon class="rotate" v-else>mdi-refresh</v-icon>
            {{ $t('message.pay_cash') }}
          </v-btn>
          <v-btn outlined class="mb-1" elevation="0" block color="primary" small @click="updateCardDialog = true"
            v-if="transaction.subscription && !transaction.cancelled && transaction.completed">
            <v-icon v-if="paymentMethod && paymentMethod.type == 'card'">mdi-credit-card-refresh-outline</v-icon>
            <v-icon v-else>mdi-bank</v-icon>
            <span v-if="paymentMethod && paymentMethod.type == 'card'">{{ $t("message.update_card") }}</span>
            <span v-else>{{ $t("message.update_payment_method") }}</span>
          </v-btn>
          <v-btn outlined class="mb-1" elevation="0" color="primary" block small
            @click="upgradeMembershipDialog = true; getAvailableMemberships()" v-if="
              transaction.subscription &&
              !transaction.cancelled && transaction.completed && !pausedUntil">
            <v-icon>mdi-chevron-double-up</v-icon>
            {{ $t("message.update_membership") }}
          </v-btn>
          <v-btn block class="mb-1" elevation="0" small :loading="isRestarting"
            v-if="transaction.cancelled && isMembership && transaction.subscription && isMember"
            @click="restartSubscriptionDialog = true" color="primary" outlined>
            {{ $t("message.restart_subscription") }}
          </v-btn>
          <v-btn outlined class="mb-1" elevation="0" block color="primary" small @click="pauseSubscriptionDialog = true"
            v-if="
              (transaction.subscription || (product && product.type == 'service')) &&
              transaction.completed &&
              !transaction.cancelled &&
              status != 'trialing' &&
              !pausedUntil
            ">
            <v-icon>mdi-pause-circle-outline</v-icon>
            {{ $t("message.pause") }}
          </v-btn>
          <v-btn class="mb-1" block
            v-if="product && product.type != 'service' && transaction.completed && !transaction.paidWithCash && selectedInvoice && totalAmount > totalRefunded"
            elevation="0" small outlined color="primary" @click="showRefundDialog()">
            <v-icon>mdi-credit-card-refund-outline</v-icon>
            {{ $t("message.refund") }}
          </v-btn>
          <v-btn class="mb-1" elevation="0" block color="error" small @click="cancelSubscriptionDialog = true" v-if="
            (transaction.subscription || (product && product.type == 'service')) &&
            !transaction.cancelled && status !== 'paused'
          ">
            <v-icon>mdi-trash-can-outline</v-icon>
            {{ $t("message.cancel") }}
          </v-btn>
          <v-btn small class="mb-1" elevation="0" block color="error" @click="cancelOneTimePayment()" v-else-if="
            product && product.type == 'good' &&
            !transaction.cancelled
          ">
            <v-icon>mdi-trash-can-outline</v-icon>
            {{ $t("message.cancel") }}
          </v-btn>
          <v-alert v-if="status === 'paused' && !transaction.cancelled" dense text type="warning" class="mb-1">
            {{ $t("message.paused_membership_cannot_be_cancelled") }}
          </v-alert>
        </div>
      </v-card>
      <v-card class="mt-2" elevation="0">
        <v-card-actions>
          <v-btn small elevation="0" @click="leave()">
            {{ $t("message.back") }}
          </v-btn>
          <v-btn v-if="product && product.type != 'service'" small elevation="0" color="secondary"
            @click="sendEmailDialog = true">
            {{ $t("message.email") }}
          </v-btn>
        </v-card-actions>
        <div outlined
          v-if="!isSmallerDisplay() && isAppAdmin($store.state.user._id) && transaction && transaction.subscription">
          <v-btn color="secondary" small elevation="0" block @click="goToSubscrption()">
            COPY STRIPE SUBSCRIPTION URL (<span class="text-overline">{{ transaction.subscription }}</span>)
          </v-btn>
        </div>
      </v-card>
      <v-card v-if="transaction.checkins && transaction.checkins.length > 0" class="mt-2" elevation="0" outlined>
        <v-card-text>
          <v-list>
            <v-subheader>
              <v-icon>mdi-map-marker-outline</v-icon>
              {{ $t("message.checkins") }}
            </v-subheader>
            <template v-for="(i, index) in transaction.checkins">
              <v-list-item :key="'checkin' + index">
                <v-list-item-action>
                  <v-icon>mdi-map-marker-outline</v-icon>
                </v-list-item-action>
                <v-list-item-content class="text-left">
                  <v-list-item-title>
                    {{ i.name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ $d(new Date(i.date), "short", $i18n.locale) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
              <v-divider v-if="index + 1 < transaction.checkins.length" :key="index"></v-divider>
            </template>
          </v-list>
        </v-card-text>
      </v-card>
    </div>
    <v-dialog v-model="cancelCashTransactionDialog" width="70%" pa-1>
      <v-card outlined>
        <v-card-title>
          <h2>{{ $t("message.remove") }} {{ $t("message.transaction") }}</h2>
        </v-card-title>
        <v-card-title>
          <div class="text-subtitle-1 font-weight-bold mr-2">{{ $t("message.are_you_sure") }}</div>
        </v-card-title>
        <v-card-actions>
          <v-btn elevation="0" small color="error" @click="removeCashTransaction()">
            {{
              $t("message.remove")
            }}
          </v-btn>
          <v-btn elevation="0" small @click="renewCashTransactionDialog = false">{{
            $t("message.close")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="updateCouponDialog" width="70%">
      <v-card outlined>
        <v-card-title class="text-subtitle-1">
          {{ $t("message.update_coupon") }}
        </v-card-title>
        <v-card-text>
          <div class="text-caption">
            {{ $t("message.update_coupon_explained") }}
          </div>
          <div class="mt-1">
            <v-switch v-model="applyDiscount" :label="$t('message.apply_discount')"></v-switch>
            <v-text-field hide-details outlined dense :disabled="discountAmount > 0" v-show="applyDiscount"
              v-model="discountPercent" :label="$t('message.discount_percent')"
              :rules="[validationRules.number]"></v-text-field>
            <v-text-field class="mt-2" hide-details :disabled="discountPercent > 0" dense outlined
              v-show="applyDiscount" v-model="discountAmount" :label="$t('message.discount_amount')"
              :rules="[validationRules.decimal]"></v-text-field>
            <v-switch v-if="applyDiscount" v-model="discountExpires" :label="$t('message.expires')"></v-switch>
            <v-text-field v-if="applyDiscount && discountExpires" class="mt-2" hide-details type="number"
              v-model="couponDurationInMonths" dense outlined :label="$t('message.duration_in_months')"></v-text-field>
            <span v-if="couponExpirationDate" class="primary--text">{{ $t('message.expiration_date') }}: {{ $d(new
              Date(couponExpirationDate),
              "month_day_year",
              $i18n.locale) }}</span>
            <div class="secondary--text text-center text-h6" v-if="applyDiscount && discountedTotal">{{
              $t('message.new_total')
            }}:
              {{ academy.currencySymbol }}{{ discountedTotal }}</div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn block outlined :loading="updatingCoupon" elevation="0" small color="primary"
            @click="updateCouponData()">
            {{ applyDiscount ? $t("message.add_coupon") : $t("message.remove_coupon") }}
          </v-btn>
        </v-card-actions>
        <v-card-actions>
          <v-btn block elevation="0" small @click="updateCouponDialog = false">{{
            $t("message.close")
            }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="renewCashTransactionDialog" width="70%">
      <v-card outlined>
        <v-card-title class="text-subtitle-1">
          {{ $t("message.renew_membership") }}
        </v-card-title>
        <v-card-text>
          <v-switch v-if="renewManualProducts.length > 1" v-model="useDifferentProduct"
            label="Different membership" dense hide-details class="mb-2 mt-0"
            @change="(val) => { if (!val) { renewProductId = null; renewCheckins = this.transaction.subscriptionCheckins || null; } }"></v-switch>
          <v-select v-if="useDifferentProduct && renewManualProducts.length > 1" outlined dense v-model="renewProductId"
            :items="renewManualProducts" item-text="text" item-value="value"
            label="Membership product" class="mb-2"
            @change="() => { if (!renewSelectedProductCheckins()) renewCheckins = null; }"></v-select>
          <div class="text-subtitle-2">
            {{ $t("message.subscription_end_date") }}
          </div>
          <DatePicker :show-time="false" v-model="renewEndDate" :past-event="false">
          </DatePicker>
          <v-text-field v-if="renewSelectedProductCheckins()" outlined dense v-model="renewCheckins"
            label="Checkin limit" :rules="[validationRules.number]" class="mt-2" clearable></v-text-field>
          <div class="mt-1">
            <v-switch v-model="applyDiscount" :label="$t('message.apply_discount')"></v-switch>
            <v-text-field outlined dense :disabled="discountAmount > 0" v-show="applyDiscount" v-model="discountPercent"
              :label="$t('message.discount_percent')" :rules="[validationRules.number]"></v-text-field>
            <v-text-field :disabled="discountPercent > 0" dense outlined v-show="applyDiscount" v-model="discountAmount"
              :label="$t('message.discount_amount')" :rules="[validationRules.decimal]"></v-text-field>
            <span class="primary--text" v-if="applyDiscount && discountedTotal">{{ $t('message.new_total') }}:
              {{ discountedTotal }}
              {{ currency }}</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined elevation="0" small color="primary" @click="renewManualMembership()">
            {{ $t("message.renew") }}
          </v-btn>
          <v-btn elevation="0" small @click="renewCashTransactionDialog = false">{{
            $t("message.close")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="cancelSubscriptionDialog" width="90%">
      <v-card outlined v-if="product">
        <v-card-title class="text-subtitle-1 font-weight-bold mr-2">
          {{ $t("message.cancel") }} {{ $t("message.subscription") }}
        </v-card-title>
        <v-card-title class="text-subtitle-2">
          <span>{{ product.name }}</span>
          <span class="ml-2 gray--text">{{ academy.currencySymbol }}{{ product.amountDecimal }}</span>
        </v-card-title>
        <v-card-text v-if="cancellationFees && cancellationFees.length > 0 && !isInfinicept && !scheduleCancellation"
          class="mb-0">
          <v-select hide-details outlined dense :disabled="!paymentMethod || paymentMethod.expired"
            :items="cancellationFees" v-model="cancellationFee" :label="$t('message.select_cancellation_fee')"
            item-text="name" item-value="value" :clearable="true">
          </v-select>
          <v-text-field outlined dense v-if="cancellationFee == 4" v-model="customCancellationFee"
            :rules="cancellationFeeRule" :label="$t('message.amount')"></v-text-field>
          <div v-if="paymentMethod && paymentMethod.expired" class="text-caption error--text">
            {{ $t("message.expired_card") }}
          </div>
        </v-card-text>
        <v-card elevation="0" outlined>
          <v-card-text class="mt-0 mb-0 pt-0 pb-0">
            <v-switch :disabled="cancellationFee ? true : false" v-model="scheduleCancellation"
              :label="$t('message.schedule_cancellation')"></v-switch>
            <div class="pa-2" v-if="scheduleCancellation && !cancellationFee">
              <div class="text-caption mb-2">{{ $t('message.schedule_cancellation_explained') }}</div>
              <div>{{ $t('message.cancellation_date') }}</div>
              <DatePicker v-model="cancellationDate" :past-event="false">
              </DatePicker>
              <v-switch prepend-icon="mdi-account-cancel-outline" v-model="cancelAndRemoveMember"
                :label="$t('message.also_remove_member_from_academy')"></v-switch>
            </div>
          </v-card-text>
        </v-card>
        <v-card-title class="mt-0">
          <v-switch v-model="cancelSubscriptionConfirm" :label="$t('message.confirm')"></v-switch>
        </v-card-title>
        <v-card-actions v-if="!isSmallerDisplay()">
          <v-btn v-if="!scheduleCancellation" small elevation="0"
            :disabled="!cancelSubscriptionConfirm || processingCancellation" color="error"
            @click="cancelSubscription()">
            <v-icon v-if="processingCancellation" class="rotate">mdi-refresh</v-icon>
            {{ $t("message.cancel") }} {{ $t("message.subscription") }}
          </v-btn>
          <v-btn v-if="!scheduleCancellation" small elevation="0"
            :disabled="!cancelSubscriptionConfirm || processingCancellation" color="error"
            @click="cancelSubscription(false, true)">
            <v-icon v-if="processingCancellation" class="rotate">mdi-refresh</v-icon>
            {{ $t("message.cancel") }} {{ $t("message.subscription") }} No Email
          </v-btn>
          <v-btn v-if="scheduleCancellation" small elevation="0"
            :disabled="!cancellationDate || processingCancellation || !cancelSubscriptionConfirm" color="error"
            @click="processScheduleCancellation()">
            <v-icon v-if="processingCancellation" class="rotate">mdi-refresh</v-icon>
            {{ $t("message.schedule_cancellation") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn small elevation="0" @click="cancelSubscriptionDialog = false">
            {{
              $t("message.close")
            }}
          </v-btn>
        </v-card-actions>
        <div v-else>
          <v-btn v-if="!scheduleCancellation" block small elevation="0"
            :disabled="!cancelSubscriptionConfirm || processingCancellation" color="error"
            @click="cancelSubscription()">
            <v-icon v-if="processingCancellation" class="rotate">mdi-refresh</v-icon>
            {{ $t("message.cancel") }} {{ $t("message.subscription") }}
          </v-btn>
          <v-btn class="mt-1" block v-if="isAppAdmin($store.state.user._id) && !scheduleCancellation" small
            elevation="0" :disabled="!cancelSubscriptionConfirm || processingCancellation" color="error"
            @click="cancelSubscription(false, true)">
            <v-icon v-if="processingCancellation" class="rotate">mdi-refresh</v-icon>
            {{ $t("message.cancel") }} {{ $t("message.subscription") }} No Email
          </v-btn>
          <v-btn class="mt-1" block v-if="scheduleCancellation" small elevation="0"
            :disabled="!cancellationDate || processingCancellation || !cancelSubscriptionConfirm" color="error"
            @click="processScheduleCancellation()">
            <v-icon v-if="processingCancellation" class="rotate">mdi-refresh</v-icon>
            {{ $t("message.schedule_cancellation") }}
          </v-btn>
          <v-btn class="mt-1" block small elevation="0" @click="cancelSubscriptionDialog = false">
            {{
              $t("message.close")
            }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog v-model="updateCardDialog" width="80%" pa-1>
      <v-card outlined>
        <v-card-title class="text-subtitle-1">
          <span v-if="paymentMethod && paymentMethod.type == 'card'">{{ $t("message.update_card") }}</span>
          <span v-else>{{ $t("message.update_payment_method") }}</span>
        </v-card-title>
        <v-card-title class="text-body-2">
          {{ $t("message.are_you_sure") }}
        </v-card-title>
        <v-card-title v-show="false">
          <v-switch v-model="saveCard" :label="$t('message.save_credit_card')"></v-switch>
        </v-card-title>
        <div v-if="paymentTypes.length > 0" v-show="true">
          <v-card-text>
            <v-select v-model="selectedPaymentOptions" :items="paymentTypes" small-chips dense attach chips
              :label="$t('message.payment_options')" multiple outlined>
            </v-select>
          </v-card-text>
        </div>
        <v-card-actions v-if="!isSmallerDisplay()">
          <v-btn outlined elevation="0" small color="primary" @click="updateCreditCard()" v-if="transaction &&
            transaction.subscription &&
            !transaction.cancelled
          ">
            <v-icon v-if="paymentMethod && paymentMethod.type == 'card'">mdi-credit-card-outline</v-icon>
            <v-icon v-else-if="paymentMethod && paymentMethod.type.indexOf('bank') >= 0">mdi-bank</v-icon>
            {{ $t("message.here") }}
          </v-btn>
          <v-btn outlined elevation="0" small :disabled="handlingCharges" color="primary"
            @click="sendUpdateNotification()" v-if="transaction &&
              transaction.subscription &&
              !transaction.cancelled && !saveCard && !isCustomAchTransaction(transaction)
            ">
            <v-progress-circular v-if="handlingCharges" indeterminate color="white"></v-progress-circular>
            <v-icon v-else>mdi-credit-card-outline</v-icon>
            {{ $t("message.send_update_notification") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn elevation="0" small :disabled="handlingCharges" @click="updateCardDialog = false">
            {{
              $t("message.close")
            }}
          </v-btn>
        </v-card-actions>
        <v-card-text v-else>
          <v-btn outlined elevation="0" small class="mt-2" color="primary" :block="true" @click="updateCreditCard()"
            v-if="transaction &&
              transaction.subscription &&
              !transaction.cancelled
            ">
            <v-icon>mdi-credit-card-outline</v-icon>
            {{ $t("message.update_card") }} {{ $t("message.here") }}
          </v-btn>
          <v-btn outlined elevation="0" small class="mt-2" :disabled="handlingCharges" color="primary" :block="true"
            @click="sendUpdateNotification()" v-if="transaction &&
              transaction.subscription &&
              !transaction.cancelled && !saveCard
            ">
            <v-progress-circular v-if="handlingCharges" indeterminate color="white"></v-progress-circular>
            <v-icon v-else>mdi-credit-card-outline</v-icon>
            {{ $t("message.send_update_notification") }}
          </v-btn>
          <v-btn elevation="0" small class="mt-2" :block="true" :disabled="handlingCharges"
            @click="updateCardDialog = false">
            {{
              $t("message.close")
            }}
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-dialog v-model="subscriptionEndDateDialog" width="90%" pa-1>
      <v-card outlined>
        <v-card-title>
          <h2>{{ $t("message.are_you_sure") }}</h2>
        </v-card-title>
        <v-card-text>
          <div class="text-subtitle-1 font-weight-bold mr-2">{{ $t("message.remove_sub_end_date_explained") }}</div>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined elevation="0" small color="primary" @click="removeSubscriptionEndDate()">OK</v-btn>
          <v-btn elevation="0" small @click="
            subscriptionEndDateDialog = false;
          isSubscription = true;
          ">{{ $t("message.close") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="assignBuyerDialog" width="90%" pa-1>
      <v-card outlined>
        <v-card-title>
          {{ $t("message.assign_buyer") }}
        </v-card-title>
        <v-card-text>
          <members-autocomplete :label="$t('message.member')" v-model="assignMemberId" />
        </v-card-text>
        <v-card-actions>
          <v-btn outlined elevation="0" small color="primary" @click="assignBuyer()">OK</v-btn>
          <v-btn elevation="0" small @click="assignBuyerDialog = false;">
            {{ $t("message.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="upgradeMembershipDialog" width="90%" pa-1>
      <v-card outlined>
        <v-card-title>
          <h2>{{ $t("message.update_membership") }}</h2>
        </v-card-title>
        <v-card-text v-if="transaction && transaction.coupon">
          <v-alert type="warning">
            {{ $t("message.must_remove_coupon_to_update") }}
          </v-alert>
        </v-card-text>
        <v-card-text v-else>
          <v-switch @change="checkMembershipUpgrade" v-model="skipProration"
            :label="$t('message.skip_proration')"></v-switch>
          <v-select :loading="loadingAvailableMemberships" outlined dense :items="availableMemberships"
            @change="checkMembershipUpgrade" clearable hide-details v-model="newMembership"
            :label="$t('message.memberships')" item-text="text" item-value="value">
          </v-select>
          <v-card-text v-if="newMembership" class="pt-0">
            <div class="text-center" v-if="upgradeData.loading">
              <v-progress-circular size="50" indeterminate color="primary"></v-progress-circular>
            </div>
            <div v-else>
              <div class="text-h6">
                {{ $t("message.next_invoice") }}
              </div>
              <div>
                <span class="mr-1 text-subtitle-2"> {{ $t("message.date") }}:</span>
                <span>{{ $d(new Date(upgradeData.date), "short", $i18n.locale) }}</span>
              </div>
              <div>
                <span class="mr-1 text-subtitle-2"> {{ $t("message.amount") }}:</span>
                <span>{{ getCurrencySymbol(currency) }}{{ upgradeData.amount }}</span>
                <span v-if="upgradeData.hasProration" class="primary--text text-caption ml-2 mb-1">{{
                  $t('message.includes_prorated_amount') }}</span>
              </div>
            </div>
          </v-card-text>
          <div class="text-caption">
            <div>
              <v-icon size="15" class="mr-1">mdi-information-outline</v-icon>
              {{ $t("message.upgrade_membership_explained") }}
            </div>
            <div>
              <v-icon size="15" class="mr-1">mdi-information-outline</v-icon>
              {{ $t("message.no_amount_if_paused") }}
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined elevation="0" :loading="upgradingMembership" small
            :disabled="!newMembership || upgradeData.loading || upgradeData.amount < 0" color="primary"
            @click="upgradeMembership()">
            {{ $t("message.submit") }}
          </v-btn>
          <v-btn elevation="0" small @click="
            upgradeMembershipDialog = false;">{{ $t("message.close") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="sendEmailDialog" width="90%" pa-1>
      <v-card outlined>
        <v-card-title>
          {{ $t("message.email_receipt") }}
        </v-card-title>
        <v-card-text>
          <div>
            <v-switch v-model="altEmailConfirm" :label="$t('message.use_different_email')"></v-switch>
          </div>
          <div>
            <v-text-field dense outlined :disabled="!altEmailConfirm" single-line outline :label="$t('message.email')"
              v-model="altEmail"></v-text-field>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined elevation="0" small :disabled="false" color="primary" @click="emailReceipt()">
            {{
              $t("message.email")
            }}
          </v-btn>
          <v-btn elevation="0" small @click="sendEmailDialog = false; selectedInvoice = null;">
            {{
              $t("message.close")
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="editTrialEndDateDialog" width="80%">
      <v-card outlined>
        <v-card-title>
          {{ $t('message.update_subscription_start_date') }}
        </v-card-title>
        <v-card-text>
          <DatePicker :show-time="false" v-model="trialPeriodDate" :past-event="false">
          </DatePicker>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined elevation="0" small color="primary" @click="updateTrialEndDate()">
            {{ $t("message.update") }}
          </v-btn>
          <v-btn elevation="0" small @click="editTrialEndDateDialog = false">
            {{
              $t("message.cancel")
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="updateItemsDialog" width="90%" persistent>
      <v-card outlined>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>{{ $t("message.update_items") }}</span>

          <v-btn-toggle v-if="transaction && transaction.items && transaction.items.length > 0"
            v-model="activeUpdateMode" mandatory color="primary" class="ml-4" dense>
            <v-btn value="add" small>
              <v-icon left small>mdi-plus</v-icon>
              {{ $t('message.add') }}
            </v-btn>
            <v-btn value="remove" small>
              <v-icon left small>mdi-pencil-outline</v-icon>
              {{ $t('message.update') }}
            </v-btn>
          </v-btn-toggle>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pt-4" v-if="transaction">
          <div v-if="activeUpdateMode === 'add'">
            <v-autocomplete v-model="selectedProductModel" :items="availableSubscriptions"
              :label="$t('message.pick_subscription')" item-text="text" item-value="value" clearable outlined dense
              @change="addItemToUpdatePending">
              <template v-slot:item="data">
                <v-list-item-content v-if="data.item">
                  <v-list-item-title v-html="data.item.text"></v-list-item-title>
                  <v-list-item-subtitle>
                    {{ academy.currencySymbol }}{{ data.item.amountDecimal }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </div>

          <v-list v-if="updateItemsDisplayList.length > 0" subheader two-line>
            <v-subheader>
              {{ activeUpdateMode === 'add' ? $t("message.new_items_to_be_added") :
                $t("message.adjust_current_quantities") }}
            </v-subheader>

            <v-list-item v-for="(item) in updateItemsDisplayList" :key="item.productId"
              :class="getItemActionState(item) === 'remove' ? 'error lighten-5' : ''">
              <v-list-item-content>
                <v-list-item-title
                  :class="getItemActionState(item) === 'remove' ? 'text-decoration-line-through error--text' : ''">
                  {{ item.name }}
                  <v-chip v-if="getItemActionState(item) === 'remove'" x-small color="error" label class="ml-2">
                    {{ $t('message.to_be_removed') }}
                  </v-chip>
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ academy.currencySymbol }}{{ item.pricePerUnit }} x {{ item.quantity }}
                </v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action class="flex-row align-center">
                <v-btn icon small @click="changeUpdateItemQuantity(item, -1)">
                  <v-icon color="red">mdi-minus-circle-outline</v-icon>
                </v-btn>
                <span class="mx-2 font-weight-bold">{{ item.quantity }}</span>
                <v-btn icon small @click="changeUpdateItemQuantity(item, 1)">
                  <v-icon color="green">mdi-plus-circle-outline</v-icon>
                </v-btn>

                <v-btn v-if="activeUpdateMode === 'add'" icon small @click="removePendingAdd(item.productId)"
                  class="ml-2">
                  <v-icon color="grey">mdi-delete</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <v-alert v-if="hasUpdateItemsChanges" colored-border border="left" color="primary"
            class="mt-4 elevation-1">
            <div v-if="!previewData" class="text-center py-2">
              <v-progress-circular indeterminate size="20" width="2" color="primary"></v-progress-circular>
            </div>
            <template v-else>
              <div class="d-flex justify-space-between align-center">
                <div>
                  <div class="text-caption grey--text">{{ $t('message.current_monthly_payment') }}</div>
                  <div class="text-h6" :class="{ 'grey--text': previewLoading }">
                    {{ academy.currencySymbol }}{{ previewData.currentMonthlyPayment.toFixed(2) }}
                  </div>
                </div>
                <v-icon>mdi-arrow-right</v-icon>
                <div class="text-right">
                  <div class="text-caption primary--text font-weight-bold">{{ $t('message.new_monthly_payment') }}
                  </div>
                  <div class="text-h6 primary--text font-weight-bold" :class="{ 'grey--text': previewLoading }">
                    {{ academy.currencySymbol }}{{ previewData.newMonthlyPayment.toFixed(2) }}
                  </div>
                </div>
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="d-flex justify-space-between align-center">
                <div class="text-caption grey--text">
                  {{ previewData.prorationAmount < 0 ? $t('message.one_time_credit') :
                    $t('message.one_time_proration') }}
                </div>
                <div class="text-subtitle-1" :class="{ 'grey--text': previewLoading }">
                  {{ academy.currencySymbol }}{{ Math.abs(previewData.prorationAmount).toFixed(2) }}
                </div>
              </div>
              <div class="text-caption grey--text mt-1">
                {{ $t('message.applied_next_invoice_explained') }}
              </div>
            </template>
          </v-alert>
        </v-card-text>

        <v-card-text v-else class="text-center py-8">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </v-card-text>

        <v-card-text>
          <v-alert type="info">
            {{ $t("message.update_subscription_items_explained") }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" outlined :loading="confirmingItemsUpdate" :disabled="!hasUpdateItemsChanges"
            @click="confirmUpdateItems()">
            {{ $t("message.confirm") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn elevation="0" @click="closeUpdateItemsDialog">
            {{ $t("message.cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="pauseSubscriptionDialog" width="90%">
      <v-card outlined>
        <v-card-title>{{ $t("message.pause_until") }}</v-card-title>
        <div v-if="status == 'past_due'">
          <v-card-text>
            <v-icon class="mr-1">mdi-alert-outline</v-icon>
            {{ $t("message.cant_pause_past_due") }}
          </v-card-text>
          <v-card-actions v-if="!isSmallerDisplay()">
            <v-btn elevation="0" small @click="pauseSubscriptionDialog = false">
              {{
                $t("message.cancel")
              }}
            </v-btn>
          </v-card-actions>
          <div v-else>
            <v-btn class="mt-1" block elevation="0" small @click="pauseSubscriptionDialog = false">
              {{
                $t("message.cancel")
              }}
            </v-btn>
          </div>
        </div>
        <div v-else>
          <v-card-text>
            <DatePicker :show-time="false" v-model="subscriptionResumeDate" :past-event="false">
            </DatePicker>
            <div>
              <v-switch v-model="sendMembershipPausedEmail"
                :label="$t('message.send_membership_paused_email')"></v-switch>
            </div>
            <div>
              <v-switch v-model="schedulePause" :label="$t('message.schedule_pause')"></v-switch>
              <div class="pa-2" v-if="schedulePause">
                <div class="text-caption mb-2">{{ $t('message.schedule_pause_explained') }}</div>
                <div>{{ $t('message.pause_date') }}</div>
                <DatePicker v-model="pauseDate" :past-event="false">
                </DatePicker>
              </div>
            </div>
          </v-card-text>
          <v-card-actions v-if="!isSmallerDisplay()">
            <v-btn :loading="pausingSubscription" outlined elevation="0" small color="primary"
              @click="pauseSubscription()">
              <v-icon>mdi-pause-circle-outline</v-icon>
              {{ schedulePause && pauseDate ? $t("message.schedule_pause") : $t("message.pause") }}
            </v-btn>
            <v-btn elevation="0" small @click="pauseSubscriptionDialog = false">
              {{
                $t("message.cancel")
              }}
            </v-btn>
          </v-card-actions>
          <div v-else>
            <v-btn outlined block elevation="0" small color="primary" @click="pauseSubscription()">
              <v-icon>mdi-pause-circle-outline</v-icon>
              {{ schedulePause && pauseDate ? $t("message.schedule_pause") : $t("message.pause") }}
            </v-btn>
            <v-btn class="mt-1" block elevation="0" small @click="pauseSubscriptionDialog = false">
              {{
                $t("message.cancel")
              }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog v-model="transactionDateDialog" max-width="90%">
      <v-card v-if="transaction" outlined>
        <v-card-title class="text-title">
          {{ $t('message.date') }}
        </v-card-title>
        <v-card-text class="text-subtitle-1">
          <DatePicker :no-boundries="true" v-model="createdDate"></DatePicker>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined small elevation="0" color="primary" @click.stop="updateTransactionDate()">{{
            $t('message.update')
          }}</v-btn>
          <v-btn class="outlined-btn" small elevation="0" @click.stop="transactionDateDialog = false">{{
            $t('message.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="resumeSubscriptionDialog" width="90%">
      <v-card outlined>
        <v-card-title>{{ $t("message.are_you_sure") }}</v-card-title>
        <v-card-actions>
          <v-btn outlined elevation="0" small color="primary" @click="resumeSubscription()">
            <v-icon>mdi-play-circle-outline</v-icon>
            {{ $t("message.resume") }}
          </v-btn>
          <v-btn elevation="0" small @click="resumeSubscriptionDialog = false">
            {{
              $t("message.cancel")
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="cancelPaymentIntentDialog" width="90%">
      <v-card outlined v-if="selectedInvoice">
        <v-card-title class="text-subtitle-1">{{ $t("message.are_you_sure") }}</v-card-title>
        <v-card-text v-if="selectedInvoice.paidWithCash" class="text-body-1">{{
          $t("message.invoice_paid_with_cash_explained") }}</v-card-text>
        <v-card-actions>
          <v-btn outlined v-if="selectedInvoice.paidWithCash" elevation="0" small color="primary"
            @click="cancelPaymentIntent()">
            {{ $t("message.paid_with_cash") }}
          </v-btn>
          <v-btn outlined v-else elevation="0" small color="primary" @click="cancelPaymentIntent()">
            {{ $t("message.void_invoice") }}
          </v-btn>
          <v-btn elevation="0" small @click="selectedInvoice = null; cancelPaymentIntentDialog = false">
            {{
              $t("message.cancel")
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="restartSubscriptionDialog" width="90%">
      <v-card outlined>
        <v-card-title>{{ $t("message.restart_subscription") }}</v-card-title>
        <v-card-text class="text-body-1">
          <div>
            {{ $t("message.restart_subscription_explained") }}
          </div>
          <v-switch v-model="restartSubscriptionConfirm" :label="$t('message.i_understand')"></v-switch>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined elevation="0" :disabled="!restartSubscriptionConfirm" small color="primary"
            @click="restartSubscription()">
            {{ $t("message.restart_subscription") }}
          </v-btn>
          <v-btn elevation="0" small @click="restartSubscriptionDialog = false">
            {{
              $t("message.cancel")
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="billingDateDialog" width="80%">
      <v-card outlined v-if="transaction">
        <v-card-title>{{ $t("message.update") }} {{ $t("message.membership_billing_date") }}</v-card-title>
        <v-card-text v-if="transaction.pausedUntil || transaction.pauseDate">
          <v-alert type="info">{{ $t('message.billing_date_update_blocked_paused') }}: </v-alert>
        </v-card-text>
        <v-card-text v-else-if="transaction.updateBillingDate">
          <div class="text-subtitle-1">{{ $t('message.scheduled_update') }}: </div>
          <div>
            <span>{{ $t('message.of_every_month', [new Date(transaction.updateBillingDate.date).getDate()]) }}</span>
            <v-switch disabled hide-details v-model="transaction.updateBillingDate.skipProration"
              :label="$t('message.charge_full_amount')"></v-switch>
          </div>
        </v-card-text>
        <v-card-text v-else>
          <div>
            <span class="mr-1 text-subtitle-1">{{ $t('message.new_billing_date') }}: </span>
            <span>{{ $t('message.of_every_month', [newBillingDate.getDate()]) }}</span>
          </div>
          <div>
            <DatePicker :no-boundries="true" v-model="newBillingDate"></DatePicker>
          </div>
          <div>
            <v-switch hide-details v-model="skipProration" :label="$t('message.charge_full_amount')"></v-switch>
          </div>
          <v-alert v-if="!isSameDate(new Date(), newBillingDate) && skipProration" type="info">{{
            $t('message.update_billing_date_future_explained_full') }}</v-alert>
          <v-alert v-else-if="!isSameDate(new Date(), newBillingDate)" type="info">{{
            $t('message.update_billing_date_future_explained_prorated')
          }}</v-alert>
          <v-alert v-else-if="skipProration" type="info">{{
            $t('message.update_billing_date_explained_full') }}</v-alert>
          <v-alert v-else type="info">{{ $t('message.update_billing_date_explained')
          }}</v-alert>
          <v-alert type="info">{{ $t('message.schedule_billing_date_update_explained')
          }}</v-alert>
          <v-switch hide-details v-model="updateBillingDateConfirm" :label="$t('message.i_understand')"></v-switch>
        </v-card-text>
        <v-card-actions v-if="!transaction.pausedUntil && !transaction.pauseDate">
          <v-btn v-if="transaction.updateBillingDate" elevation="0" small @click="updateBillingDate(true)">
            <v-icon v-if="updatingBillingCycle" class="rotate">mdi-refresh</v-icon>
            {{ $t("message.cancel_update") }}
          </v-btn>
          <v-btn v-else outlined elevation="0" small :disabled="!updateBillingDateConfirm" color="primary"
            @click="updateBillingDate()">
            <v-icon v-if="updatingBillingCycle" class="rotate">mdi-refresh</v-icon>
            {{ $t("message.update") }}
          </v-btn>
          <v-btn elevation="0" small @click="billingDateDialog = false">
            {{
              $t("message.cancel")
            }}
          </v-btn>
        </v-card-actions>
        <v-card-actions v-else>
          <v-btn elevation="0" small @click="billingDateDialog = false">
            {{
              $t("message.cancel")
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editSubscriptionCheckinsDialog" width="80%">
      <v-card outlined>
        <v-card-title>{{ $t("message.update") }} {{ $t("message.subscription_checkins") }}</v-card-title>
        <v-card-text v-if="transaction">
          <v-text-field dense outlined v-model="transaction.subscriptionCheckins"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined elevation="0" small color="primary" @click="updateCheckinsDate()">
            {{ $t("message.update") }}
          </v-btn>
          <v-btn elevation="0" small @click="editSubscriptionCheckinsDialog = false">
            {{
              $t("message.cancel")
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="editPaidWithDialog" max-width="600">
      <v-card v-if="transaction" style class="text-xs-left pa-3">
        <v-card-title>{{ $t('message.paid_with') }}</v-card-title>
        <v-card-text>
          <PaymentTypeSelect v-model="transaction.paidWith"></PaymentTypeSelect>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined elevation="0" color="primary" @click="updatePaidWith()">{{ $t("message.update") }}</v-btn>
          <v-btn elevation="0" @click="refundConfirmDialog = false">{{ $t('message.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="refundConfirmDialog" max-width="600">
      <v-card style class="text-xs-left pa-3">
        <v-card-title>{{ $t('message.refund') }}</v-card-title>
        <v-card-text class="text-body">
          {{ $t('message.refund_confirm') }}
        </v-card-text>
        <v-card-text>
          <v-text-field :label="$t('message.amount')" outline v-model="refundAmount"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn :loading="refunding" outlined elevation="0" color="primary" @click="refund()">{{ $t('message.refund')
          }}</v-btn>
          <v-btn elevation="0" color="error" @click="refundConfirmDialog = false">{{ $t('message.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <div style="display:none" ref="print_div" v-if="academy && transaction">
      <div style="font-size: 1.5em;text-align: center; font-family: Avenir, Arial, sans-serif">
        <h2>{{ $t("message.purchase_receipt") }}</h2>
        <h4 style="color:gray">{{ academy.name }}</h4>
        <h4 style="color:gray">{{ academy.address }}</h4>
        <table style="margin-top: 50px; width: 100%">
          <tr>
            <td></td>
            <td></td>
          </tr>
          <tr>
            <td style="text-align: right">
              <b>{{ $t("message.transaction") }} #:</b>
            </td>
            <td style="text-align: left">{{ transaction._id }}</td>
          </tr>
          <tr>
            <td style="text-align: right">
              <b>{{ $t("message.date_created") }}:</b>
            </td>
            <td style="text-align: left">{{ $d(new Date(createdDate), "long", $i18n.locale) }}</td>
          </tr>
          <tr v-if="transaction.user">
            <td style="text-align: right">
              <b>{{ $t("message.processed_by") }}:</b>
            </td>
            <td style="text-align: left">{{ transaction.user.fName }} {{ transaction.user.lName }}</td>
          </tr>
          <tr>
            <td style="text-align: right">
              <b>{{ $t("message.sold_to") }}:</b>
            </td>
            <td style="text-align: left">
              {{
                transaction.buyer
                  ? transaction.buyer.fName + " " + transaction.buyer.lName
                  : email
              }}
            </td>
          </tr>
          <tr v-if="product">
            <td style="text-align: right">
              <b>{{ $t("message.product") }}:</b>
            </td>
            <td style="text-align: left">{{ product.name }}</td>
          </tr>
          <tr>
            <td style="text-align: right">
              <b>{{ $t("message.quantity") }}:</b>
            </td>
            <td style="text-align: left">{{ transaction.quantity }}</td>
          </tr>
          <tr>
            <td style="text-align: right"></td>
            <td style="text-align: left"></td>
          </tr>
          <tr v-if="transaction && transaction.coupon">
            <td style="text-align: right">
              <b>{{ $t("message.discount") }}:</b>
            </td>
            <td style="text-align: left">
              <span v-if="transaction.coupon.discountPercent">
                {{ transaction.coupon.discountPercent }}%
              </span>
              <span v-if="transaction.coupon.discountAmount">
                {{ academy.currencySymbol }}{{ transaction.coupon.discountAmount }}
              </span>
            </td>
          </tr>
          <tr>
            <td style="text-align: right">
              <b>{{ $t("message.total") }}:</b>
            </td>
            <td style="text-align: left">
              {{ totalAmount * transaction.quantity }}
              {{ (currency + "").toUpperCase() }}
            </td>
          </tr>
          <tr v-if="checkoutSession && checkoutSession.automaticTax">
            <td style="text-align: right">
              <b>{{ $t("message.tax") }}:</b>
            </td>
            <td style="text-align: left">
              {{ academy.currencySymbol }}{{ centsToAmount(checkoutSession.totalDetails.amount_tax) }}
            </td>
          </tr>
          <tr v-if="targetInvoice">
            <td style="text-align: right">
              <b>{{ $t("message.invoice") }}:</b>
            </td>
            <td style="text-align: left">{{ targetInvoice.id }}</td>
          </tr>
          <tr v-if="targetInvoice && targetInvoice.created">
            <td style="text-align: right">
              <b>{{ $t("message.invoice_date") }}:</b>
            </td>
            <td style="text-align: left">{{ $d(getInvoiceDate(targetInvoice), "long", $i18n.locale) }}</td>
          </tr>
        </table>
        <hr style="width:100%; color:gray;margin-top:20px">
        <div style="text-align:center" v-if="academyImage">
          <ImageDisplay style="width:60px;height:60px;object-fit:cover;border-radius:50%;" :src="academyImage" />
        </div>
        <div v-if="academy" style="text-align:center;color:gray">
          <h5>{{ academy.name }}</h5>
          <h6>{{ academy.address }}</h6>
        </div>
        <div style="text-align: center;margin-top:20px" class="supporting-text">
          <div>Powered by BJJLINK&copy; LLC 2023</div>
          <img style="width:50px;" :src="assets('logo_small.png')" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AcademyActivity from "@/components/displays/AcademyActivity.vue";
import PaymentMethod from "@/components/displays/PaymentMethodDisplay.vue";
import PaymentType from "@/components/displays/PaymentTypeDisplay.vue";
import TransactionIcon from "@/components/displays/TransactionIcon.vue";
import DatePicker from "@/components/inputs/DateTimePicker.vue";
import MembersAutocomplete from "@/components/inputs/MembersAutocomplete.vue";
import PaymentTypeSelect from "@/components/inputs/PaymentTypeSelect.vue";
import AvailableClasses from "@/components/inputs/ScheduleClassesCombo.vue";
import TagsCombo from "@/components/inputs/TagsCombo.vue";
import Api from "@/services/Api";
import Api2 from "@/services/Api2";
import { Environment } from "@/services/Environment";
var PHE = require("print-html-element");

export default {
  components: {
    PaymentMethod,
    TransactionIcon,
    DatePicker,
    AcademyActivity,
    MembersAutocomplete,
    AvailableClasses,
    TagsCombo,
    PaymentType,
    PaymentTypeSelect
  },
  data() {
    return {
      vm: this,
      transactionId: this.$route.params.sessionId,
      invoiceId: this.$route.query.invoiceId,
      paymentIntentId: this.$route.params.paymentIntentId,
      sessionId: this.$route.query.s,
      redirectMode: this.$route.query.m,
      sessionStatus: this.$route.query.status,
      loading: true,
      refunding: false,
      newBillingDate: new Date(),
      editTrialEndDateDialog: false,
      isSubscription: false,
      editSubscriptionCheckinsDialog: false,
      subscriptionEndDate: new Date(),
      subscriptionEndDatePickerDialog: false,
      trialEndDatePickerDialog: false,
      upgradeMembershipDialog: null,
      transaction: null,
      product: null,
      invoices: [],
      transactionItems: [],
      availableTags: [],
      tagsSearch: null,
      selectedInvoice: null,
      isMembership: false,
      processingCancellation: false,
      pausingSubscription: false,
      history: [],
      targetInvoice: null,
      updating: false,
      saveCard: false,
      editSubscriptionEndDate: false,
      trialPeriodDate: null,
      editPaidWithDialog: null,
      updateCardDialog: false,
      updateAllowedClasses: function (vm, val) {
        if (vm && val) {
          vm.updateAllowedClassesPost(val)
        }
      },
      newAltMember: null,
      createdDate: null,
      subscriptionEndDateDialog: false,
      cancelPaymentIntentDialog: null,
      amount: null,
      currency: null,
      handlingCharges: false,
      cancelSubscriptionDialog: false,
      cancelAndRemoveMember: false,
      renewCashTransactionDialog: false,
      renewEndDate: null,
      renewCheckins: null,
      renewProductId: null,
      renewManualProducts: [],
      useDifferentProduct: false,
      cancelSubscriptionConfirm: false,
      scheduleCancellation: false,
      pauseDate: null,
      schedulePause: null,
      sendMembershipPausedEmail: false,
      pauseSubscriptionDialog: false,
      pausedUntil: null,
      isInfinicept: false,
      totalRefunded: 0,
      totalAmount: 0,
      subscriptionResumeDate: null,
      resumeSubscriptionDialog: false,
      sendEmailDialog: false,
      altEmail: null,
      expirationTime: null,
      todayString: null,
      minTrialStartString: null,
      altEmailConfirm: false,
      refundConfirmDialog: null,
      totalCheckins: null,
      status: null,
      paymentMethod: null,
      refundAmount: 0,
      savedCard: null,
      email: null,
      transactionDateDialog: null,
      cancelCashTransactionDialog: false,
      cancellationDate: null,
      academy: null,
      retryingPayment: false,
      availableSubscriptions: [],
      academyImage: null,
      signedContract: null,
      expired: null,
      tooCloseToStartDate: false,
      onboarding: null,
      cancellationFee: null,
      cancellationFeeRule: null,
      customCancellationFee: null,
      cancellationFees: null,
      paymentsReady: false,
      couponDurationInMonths: null,
      couponExpirationDate: null,
      upgradingMembership: false,
      loadingInvoices: false,
      loadingAvailableMemberships: false,
      isIncompleteCustomAch: null,
      restartSubscriptionDialog: false,
      restartSubscriptionConfirm: false,
      customAchData: null,
      isRestarting: false,
      stripeAccountId: null,
      checkoutSession: null,
      selectedPaymentOptions: [],
      billingDateDialog: false,
      skipProration: false,
      updateBillingDateConfirm: false,
      updatingBillingCycle: false,
      nextBillingDate: null,
      scheduledBillingDateUpdate: null,
      paymentTypes: [],
      assignBuyerDialog: false,
      assignMemberId: null,
      taxData: { enabled: false },
      upgradeData: { loading: false },
      availableMemberships: [],
      discountExpires: false,
      processingPayment: false,
      newMembership: null,
      applyDiscount: false,
      discountPercent: null,
      discountAmount: null,
      discountedTotal: null,
      allowedClassCount: {},
      updateCouponDialog: null,
      updatingCoupon: false,
      allowedClassIntervals: [
        { text: this.$t("message.weekly"), value: "week" },
        { text: this.$t("message.monthly"), value: "month" },],
      uploadingMessage: null,
      uploading: false,
      uploadedReceipt: null,
      selectedFile: null,
      receipt: null,
      pendingUpdateCompletion: false,
      updateItemsDialog: false,
      activeUpdateMode: 'add',
      selectedProductModel: null,
      pendingAdds: [],
      pendingRemovals: [], // Stores: { productId, name, pricePerUnit, quantity, originalQuantity }
      confirmingItemsUpdate: false,
      previewData: null, // { currentMonthlyPayment, newMonthlyPayment, prorationAmount, couponActive }
      previewLoading: false,
      previewDebounceTimer: null,
      sendingReminder: false
    };
  },
  beforeRouteUpdate(to) {
    this.transactionId = to.params.memberid;
    this.init();
  },
  created() {
    this.init();
    this.getAvailableTags();
    Api()
      .get(
        "admins/academy/" +
        this.$store.state.managedAcademy +
        "/is_connected/stripe"
      )
      .then((response) => {
        this.stripeAccountId = response.data.id;
        this.paymentsReady = response.data.stripeConnected;
      });
  },
  computed: {
    updateItemsDisplayList() {
      if (!this.transaction) return [];
      return this.activeUpdateMode === 'add' ? this.pendingAdds : this.pendingRemovals;
    },

    hasUpdateItemsChanges() {
      const hasAdds = this.pendingAdds.length > 0;
      const hasUpdates = this.pendingRemovals.some(i => i.quantity !== i.originalQuantity);
      return hasAdds || hasUpdates;
    },

    pastDueInvoice() {
      if (!this.invoices || this.invoices.length === 0) return null;
      return this.invoices.find(
        (i) => i.amountPaid === 0 && !['voided', 'future_payment', 'cancelled', 'completed'].includes(i.status)
      ) || null;
    },
  },
  watch: {
    isSubscription: {
      immediate: false,
      handler: function (newValue) {
        if (!newValue) this.subscriptionEndDateDialog = true;
      },
    },
    discountPercent: {
      handler: function (newValue) {
        if (!isNaN(newValue) && this.transaction && this.transaction.quantity) {
          let originalAmount = this.transaction.amount * this.transaction.quantity;
          this.discountedTotal = this.getDiscountedPrice(originalAmount, newValue);
        }
      },
    },
    discountAmount: {
      handler: function (newValue) {
        if (!isNaN(newValue) && this.transaction && this.transaction.quantity) {
          if (this.transaction.amount > 0) {
            let originalAmount = this.transaction.amount * this.transaction.quantity;
            this.discountedTotal = this.getDiscountedPrice(originalAmount, newValue, newValue);
          } else if (this.product) {
            let originalAmount = this.product.amountDecimal * this.transaction.quantity;
            this.discountedTotal = this.getDiscountedPrice(originalAmount, newValue, newValue);
          }
        }
      },
    },
    status(val) {
      if (val === 'past_due') {
        this.getInvoices();
      }
    },
    pendingAdds: {
      deep: true,
      handler() {
        this.scheduleItemsPreview();
      },
    },
    pendingRemovals: {
      deep: true,
      handler() {
        this.scheduleItemsPreview();
      },
    },
  },
  methods: {
    sendReminder() {
      this.sendingReminder = true;
      Api()
        .post(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/reminder/transaction/" +
          this.transactionId
        )
        .then((response) => {
          if (response) {
            this.sendingReminder = false;
            this.showAlert(
              "success",
              this.$t("message.sent")
            );
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    getItemActionState(item) {
      if (this.activeUpdateMode === 'add') return 'add';
      if (item.quantity === 0) return 'remove';
      if (item.quantity !== item.originalQuantity) return 'update';
      return 'keep';
    },

    changeUpdateItemQuantity(item, change) {
      // Find the actual data source
      const targetArray = this.activeUpdateMode === 'add' ? this.pendingAdds : this.pendingRemovals;
      const targetItem = targetArray.find(i => i.productId === item.productId);

      if (targetItem) {
        const newQty = targetItem.quantity + change;
        if (newQty >= 0) {
          this.$set(targetItem, 'quantity', newQty);
        }
      }
    },

    initUpdateItemsDialog() {
      this.pendingAdds = [];
      this.selectedProductModel = null;
      this.activeUpdateMode = 'add';

      // Map existing upsales to our tracker
      if (this.transaction && this.transaction.items) {
        this.pendingRemovals = this.transaction.items.map(item => ({
          productId: item.product._id || item.product,
          name: item.name,
          pricePerUnit: item.amountDecimal,
          quantity: item.quantity,
          originalQuantity: item.quantity
        }));
      } else {
        this.pendingRemovals = [];
      }
      this.previewData = null;
      this.updateItemsDialog = true;
    },

    addItemToUpdatePending(productId) {
      if (!productId) return;
      // Don't add the main membership as an extra
      const mainProdId = this.transaction.product._id || this.transaction.product;
      if (productId === mainProdId) {
        this.selectedProductModel = null;
        return;
      }

      const existingExtra = this.pendingRemovals.find(i => i.productId === productId);
      if (existingExtra) {
        this.$set(existingExtra, 'quantity', existingExtra.quantity + 1);
        this.activeUpdateMode = 'remove';
      } else {
        const existingNew = this.pendingAdds.find(i => i.productId === productId);
        if (existingNew) {
          this.$set(existingNew, 'quantity', existingNew.quantity + 1);
        } else {
          const p = this.availableSubscriptions.find(s => s.value === productId);
          if (p) {
            this.pendingAdds.push({
              productId: p.value,
              name: p.text,
              pricePerUnit: p.amountDecimal,
              quantity: 1,
              originalQuantity: 0
            });
          }
        }
      }
      this.selectedProductModel = null;
    },

    removePendingAdd(productId) {
      this.pendingAdds = this.pendingAdds.filter(i => i.productId !== productId);
    },

    closeUpdateItemsDialog() {
      this.updateItemsDialog = false;
      this.pendingAdds = [];
      this.pendingRemovals = [];
      if (this.previewDebounceTimer) clearTimeout(this.previewDebounceTimer);
      this.previewData = null;
    },
    loadAvailableSubscriptions() {
      Api()
        .get(
          "admins/academy/" + this.$store.state.managedAcademy + "/products"
        )
        .then((response) => {
          const data = response.data;
          this.availableSubscriptions = [];
          if (data) {
            if (this.paymentsReady && data.subscriptions && data.subscriptions.length > 0) {
              for (const p of data.subscriptions) {
                if (p._id != this.transaction.product._id)
                  this.availableSubscriptions.push({ value: p._id, text: p.name, frequency: p.interval, automatic: true, amountDecimal: p.amountDecimal, customDaysInterval: p.customDaysInterval });
              }
            }
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    buildItemsToUpdatePayload() {
      // New items
      const adds = this.pendingAdds.map(i => ({
        productId: i.productId,
        action: 'add',
        quantity: i.quantity
      }));

      // Changed existing items
      const updates = this.pendingRemovals
        .filter(i => i.quantity !== i.originalQuantity)
        .map(i => ({
          productId: i.productId,
          action: i.quantity === 0 ? 'remove' : 'add',
          quantity: i.quantity
        }));

      return [...adds, ...updates];
    },
    scheduleItemsPreview() {
      if (this.previewDebounceTimer) clearTimeout(this.previewDebounceTimer);
      if (!this.hasUpdateItemsChanges) {
        this.previewData = null;
        return;
      }
      this.previewDebounceTimer = setTimeout(() => {
        this.fetchItemsPreview();
      }, 500);
    },
    fetchItemsPreview() {
      this.previewLoading = true;
      const payload = { itemsToUpdate: this.buildItemsToUpdatePayload() };
      Api()
        .post("payments/academy/" +
          this.$store.state.managedAcademy +
          "/subscription/" + this.transactionId + "/update/items/preview", payload)
        .then((response) => {
          this.previewData = response.data;
          this.previewLoading = false;
        })
        .catch(() => {
          this.previewLoading = false;
        });
    },
    confirmUpdateItems() {
      this.confirmingItemsUpdate = true;

      const payload = {
        prorationBehavior: 'create_prorations',
        itemsToUpdate: this.buildItemsToUpdatePayload()
      };
      Api()
        .patch("payments/academy/" +
          this.$store.state.managedAcademy +
          "/subscription/" + this.transactionId + "/update/items", payload)
        .then((response) => {
          const data = response.data;
          if (data) {
            this.showAlert("success", this.$t("message.success"));
            this.getTransactionData();
            this.getInvoices(true);
            this.closeUpdateItemsDialog();
            this.confirmingItemsUpdate = false;
          }
        })
        .catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
          this.confirmingItemsUpdate = false;
        });
    },
    unCancelTransaction() {
      Api()
        .patch(
          "bjjlinkadmins/managedacademies/transactions/" + this.transactionId + "/uncancel"
        )
        .then(() => {
          this.getTransactionData();
          this.showAlert("success", this.$t("message.updated"));
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    goToSubscrption() {
      if (this.transaction && this.transaction.subscription) {
        this.copyToClipboard('https://dashboard.stripe.com/acct_1FXgrUB0R3AacMkx/connect/accounts/' + this.stripeAccountId + '/subscriptions/' + this.transaction.subscription);
      }
    },
    filePicker() {
      this.$refs.fileUploadInput.click();
    },
    onFileSelect(event) {
      this.uploading = true;
      if (event.target.files[0]) {
        this.receipt = null;
        this.selectedFile = event.target.files[0];
        const size = event.target.files[0].size;
        const sizeMb = Math.floor(size / 1000000);
        if (sizeMb <= 15) {
          this.uploadPhoto();
        } else {
          this.uploading = false;
          this.showAlert(
            "error",
            "Sorry! Your image is too big (" +
            sizeMb +
            "mb), try a smaller one."
          );
        }
      } else {
        this.uploading = false;
      }
    },
    uploadPhoto() {
      const formData = new FormData();
      this.uploadingMessage = this.$t("message.image_upload_progress");
      const d = new Date();
      const millis = d.getTime();
      const imgExtension = this.selectedFile.type.toLowerCase().split('/')[1];
      const blob = new Blob([this.selectedFile], { type: this.selectedFile.type });
      formData.append("image", blob, 'bjjlink-user-admin-uploaded-image-' + millis + '.' + imgExtension);
      Api2().post("content/image/admin", formData)
        .then(res => {
          const data = res.data;
          if (data) {
            const vm = this;
            setTimeout(function () {
              vm.receipt = data.original;
              vm.uploadedReceipt = data;
              vm.uploadingMessage = null;
              vm.uploading = false;
            }, 4000);
          }
        })
        .catch(() => {
          this.showAlert("error", "Oops!", ":(");
          this.uploading = false;
        });
    },
    updateTransactionReceipt() {
      Api()
        .patch(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/transactions/" +
          this.transactionId + "/receipt", { receipt: this.uploadedReceipt.location }
        )
        .then(() => {
          this.showAlert("success", this.$t("message.updated"));
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("messaerror_generic"));
        });
    },
    updatePaidWith() {
      Api()
        .patch(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/transactions/" +
          this.transactionId + "/paid-with", { paidWith: this.transaction.paidWith }
        )
        .then(() => {
          this.showAlert("success", this.$t("message.updated"));
          this.editPaidWithDialog = false;
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    updateTags() {
      Api()
        .patch(
          "payments/managedacademy/" +
          this.$store.state.managedAcademy +
          "/transactions/" +
          this.transactionId +
          "/tags",
          { tags: this.transaction.tags }
        )
        .then((response) => {
          if (response.data) {
            this.showAlert("success", this.$t("message.success"));
          }
        });
    },
    getAvailableTags() {
      Api2()
        .get("admins/academy/" + this.$store.state.selectedAcademy + "/tags")
        .then((response) => {
          if (response.data) {
            this.availableTags = response.data.sort();
          }
        })
        .catch((e) => {
          this.loading = false;
          if (e)
            this.showAlert("error", "Oops!", this.$t("message.error_generic"));
        });
    },
    updateAltMembersCount() {
      console.log(this.transaction.altMembersCount);
      Api2()
        .patch(
          "payments/managedacademy/" +
          this.$store.state.managedAcademy +
          "/transactions/" +
          this.transactionId + "/altmembers",
          { altMembersCount: this.transaction.altMembersCount }
        )
        .then(() => {
          this.showAlert("success", this.$t("message.updated"));
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    completePurchase(id) {
      this.updating = true;
      Api()
        .patch(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/payments/complete/checkout/" +
          id
        )
        .then((response) => {
          if (response) {
            this.showAlert(
              "success",
              this.$t("message.transaction_completed")
            );
            this.updating = false;
          }
        }).catch((err) => {
          this.updating = false;
          if (err.response.status != 304)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    assignBuyer() {
      this.assignBuyerDialog = false;
      Api()
        .patch(
          "payments/managedacademy/" +
          this.$store.state.managedAcademy +
          "/transaction/" +
          this.transactionId + "/buyer/" + this.assignMemberId
        )
        .then(() => {
          this.getTransactionData();
          this.showAlert("success", this.$t("message.updated"));
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    updateManualRecurrence() {
      Api()
        .patch(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/transactions/" +
          this.transactionId + "/manual-recurrence", { manualRecurrence: this.transaction.manualRecurrence }
        )
        .then(() => {
          this.showAlert("success", this.$t("message.updated"));
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    createRecurrentEntry() {
      Api()
        .post(
          "payments/v3/managedacademy/" +
          this.$store.state.managedAcademy +
          "/transactions/" +
          this.transaction._id + "/recurrent/entry"
        )
        .then(() => {
          this.showAlert("success", this.$t("message.success"));
        })
        .catch(() => {
          this.processingPayment = false;
          this.showAlert("error", "Declined");
        });
    },
    completePaymentCash() {
      if (this.processingPayment)
        return;
      //"/academy/:managedAcademyId/transactions/:transactionId/onetime/savedcard"
      this.processingPayment = true;
      Api()
        .post(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/transactions/" +
          this.transaction._id + "/onetime/cash"
        )
        .then(() => {
          this.loading = true;
          this.processingPayment = false;
          this.paymentMethod = null;
          this.transaction.completed = new Date();
          this.status = 'paid';
          this.transaction.paidWithCash = true;
          this.loading = false;
          this.$router.go();
          this.showAlert("success", this.$t("message.success"));
        })
        .catch(() => {
          this.processingPayment = false;
          this.showAlert("error", "Declined");
        });
    },
    completePayment() {
      if (this.transaction.paymentsModule == 'infinicept') {
        const postData = { routeFrom: 'viewtransaction', transactionId: this.transactionId };
        this.navigateTo({
          name: "inifnicept-checkout",
          params: { transactionData: postData, transactionId: this.transactionId }
        });
      } else if (this.isCustomAchTransaction(this.transaction)) {
        this.processCustomAchTransaction();
      } else {
        this.navigateTo({
          name: 'striperedirect_transaction',
          params: { transactionId: this.transactionId },
        });
      }
    },
    async processCustomAchTransaction() {
      this.handlingCharges = true;
      if (!this.customAchData || !this.customAchData.clientSecret) {
        this.showAlert("error", "Invalid data to complete ACH payment. Contact Suppport");
        this.handlingCharges = false;
        return;
      }
      /*eslint-disable no-undef*/
      let stripe = Stripe(Environment.get("VUE_APP_STRIPE_PUBLISHABLE_KEY"), {
        stripeAccount: this.stripeAccountId ? this.stripeAccountId : this.$store.state.stripeAccountId
      });
      if (this.transaction.paymentType == 'acss_debit') {
        const { paymentIntent, error } = await stripe.confirmAcssDebitPayment(
          this.customAchData.clientSecret,
          {
            payment_method: {
              billing_details: {
                name: this.transaction.buyer.displayName,
                email: this.transaction.email,
              },
            },
          }
        );
        if (error) {
          console.error('Error processing acss_debit: ', error);
          this.handlingCharges = false;
          this.showAlert("error", "Error processing Debit payment with ", this.transaction.paymentType);
        } else if (paymentIntent && paymentIntent.status != 'canceled') {
          console.log('PaymentIntent: ', paymentIntent);
          this.completeCustomAchPurchase();
        }
      } else if (this.selectedPaymentOptions.indexOf('nz_bank_account') >= 0) {
        console.log('Element for nz_bank_account');
        this.handlingCharges = false;
        this.showAlert("error", "nz_bank_account Not implemented yet. Stay tuned");
      } else {
        this.showAlert("error", "Invalid ACH payment type: ", this.selectedPaymentOptions);
        this.handlingCharges = false
        return;
      }
    },
    completeCustomAchPurchase() {
      //Send it to the back end to record our transaction
      const postData = {
        transactionId: this.transactionId
      };
      Api()
        .post(
          "payments/managedacademy/" +
          this.$store.state.managedAcademy +
          "/ach/complete/subscription", postData
        )
        .then((response) => {
          if (response.data) {
            this.showAlert(
              "success",
              this.$t("message.payment_completed")
            );
            this.isIncompleteCustomAch = null;
            const vm = this;
            setTimeout(function () {
              vm.handlingCharges = false;
              vm.getTransactionData();
            }, 1000);
          }
        }).catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
          this.handlingCharges = false;
        });
    },
    updateAllowedClassesPost(val, prop) {
      let postData = {
      };
      if (prop == 'count') {
        postData.prop = 'allowedClassCount'
        if (this.allowedClassCount && this.allowedClassCount.interval && this.allowedClassCount.count > 0)
          postData.allowedClassCount = this.allowedClassCount;
        else
          postData.allowedClassCount = null;
      } else if (prop == 'allowedCustomClasses') {
        postData.prop = 'allowedCustomClasses'
        postData.allowedCustomClasses = this.trimStrings(this.transaction.allowedCustomClasses);
      } else {
        postData.prop = 'allowedClasses'
        postData.allowedClasses = val;
      }
      Api()
        .patch(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/transaction/" +
          this.transactionId + "/allowed-classes", postData
        )
        .then((response) => {
          const data = response.data;
          if (data) {
            this.showAlert("success", this.$t("message.updated"));
          }
        })
        .catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    checkMembershipUpgrade() {
      if (this.newMembership) {
        this.upgradeData.loading = true;
        Api2()
          .post("payments/academy/" +
            this.$store.state.managedAcademy +
            "/subscription/" + this.transactionId + "/upgrade/check", {
            product: this.newMembership,
            prorationBehavior: this.skipProration ? 'none' : 'create_prorations'
          })
          .then((response) => {
            const data = response.data;
            if (data && data.upcomingInvoice) {
              this.upgradeData = {
                price: data.price,
                date: data.upcomingInvoice.date,
                amount: data.upcomingInvoice.amount,
                hasProration: data.upcomingInvoice.hasProration,
              };
            }
          })
          .catch(() => {
            this.showAlert("error", this.$t("message.error_generic"));
          });
      } else {
        this.upgradeData = { loading: false };
      }
    },
    upgradeMembership() {
      if (this.newMembership) {
        this.upgradingMembership = true;
        Api()
          .patch("payments/academy/" +
            this.$store.state.managedAcademy +
            "/subscription/" + this.transactionId + "/upgrade", {
            product: this.newMembership,
            price: this.upgradeData.price,
            removeCoupon: true,//this.removeCoupon,
            prorationBehavior: this.skipProration ? 'none' : 'create_prorations'
          })
          .then((response) => {
            const data = response.data;
            if (data) {
              this.showAlert("success", this.$t("message.success"));
              this.getTransactionData();
              this.upgradeMembershipDialog = false;
              this.newMembership = null;
              this.getInvoices(true);
              this.upgradeData = { loading: false };
              this.upgradingMembership = false;
            }
          })
          .catch(() => {
            this.showAlert("error", this.$t("message.error_generic"));
            this.upgradingMembership = false;
          });
      }
    },
    getAvailableMemberships() {
      this.loadingAvailableMemberships = true;
      Api2()
        .get(
          "admins/academy/" + this.$store.state.managedAcademy + "/products"
        )
        .then((response) => {
          const data = response.data;
          if (data && this.product) {
            this.availableMemberships = [];
            //availableMemberships
            const currency = this.academy.currencySymbol;
            for (const i of data.subscriptions) {
              let taxMatches = true;
              if (this.product.taxBehavior && i.taxBehavior != this.product.taxBehavior)
                taxMatches = false;
              if (i.interval == this.product.interval && i._id != this.product._id && taxMatches) {
                this.availableMemberships.push({ text: i.name + ' (' + (currency + i.amountDecimal) + ')', value: i._id })
              }
            }
            if (this.availableMemberships.length == 0) {
              this.availableMemberships.push({
                text: this.$t("message.no_upgrade_available"),
                value: null
              });
            }
            this.loadingAvailableMemberships = false;
          }
        })
        .catch((e) => {
          console.log(e)
          this.loading = false;
          if (e)
            this.showAlert(
              "error",
              this.$t("message.error_generic")
            );
        });
    },
    showCancelPaymentIntentDialog(i, paidWithCash) {
      this.selectedInvoice = i;
      if (paidWithCash)
        this.selectedInvoice.paidWithCash = true;
      else
        this.selectedInvoice.paidWithCash = false;
      this.cancelPaymentIntentDialog = true;
    },
    cancelPaymentIntent() {
      const paidWithCash = this.selectedInvoice.paidWithCash;
      const paymentIntentId = this.selectedInvoice.paymentIntent;
      if (paidWithCash && !this.product) {
        this.showAlert("error", this.$t("message.contact_support"));
        return;
      }
      Api()
        .patch(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/subscription/" +
          this.transactionId + "/cancel/invoice",
          {
            paymentIntent: paymentIntentId,
            paidWithCash: paidWithCash,
            invoice: this.selectedInvoice.id
          }
        )
        .then((response) => {
          if (response.data) {
            this.selectedInvoice = null;
            this.cancelPaymentIntentDialog = false;
            if (paidWithCash && this.transaction.paymentsModule != 'infinicept') {
              //Sell custom amount
              const items = [
                {
                  name: this.product.name,
                  description: this.product.description,
                  images: this.product.image ? [this.product.image] : null,
                  amountDecimal: this.totalAmount,
                  currency: this.currency,
                  quantity: 1,
                },
              ];
              const postData = {
                items: items,
                processedBy: "desk",
                buyer: this.transaction.buyer._id,
                quantity: 1,
                email: this.transaction.email,
                existingTransaction: this.transactionId,
                category: 'membership'
              };
              const postUrl =
                "payments/academy/" +
                this.$store.state.managedAcademy +
                "/onetime/cash/noproduct";
              Api()
                .post(postUrl, postData)
                .then((response) => {
                  const data = response.data;
                  if (data) {
                    this.showAlert("success", this.$t("message.success"));
                    this.getInvoices(true);
                  }
                })
                .catch(() => {
                  this.showAlert("error", this.$t("message.error_generic"));
                });
            } else {
              this.showAlert("success", this.$t("message.success"));
              this.getInvoices(true);
            }
          }
        })
        .catch((err) => {
          const msg = err && err.response && err.response.data && err.response.data.message;
          this.showAlert("error", msg || this.$t("message.contact_support"));
        });
    },
    canCancelInvoice(i) {
      if (this.status == 'cancelled' || this.status == 'canceled') {
        return false;
      }
      if (i.status == "uncollectible") {
        return false;
      } else if (i && i.paymentIntentStatus) {
        if (i.paymentIntentStatus == 'requires_capture' ||
          i.paymentIntentStatus == 'requires_payment_method' ||
          i.paymentIntentStatus == 'requires_capture' ||
          i.paymentIntentStatus == 'requires_confirmation' ||
          i.paymentIntentStatus == 'requires_action' ||
          i.paymentIntentStatus == 'pending_payment' ||
          i.paymentIntentStatus == 'processing')
          return true;
      } else if (i.status == 'DECLINED') {
        return true;
      }
      return false;
    },
    canPause() {
      if (this.transaction.paymentsModule == 'easypay')
        return false;
      if ((this.transaction.subscription || (this.product && this.product.type == 'service')) &&
        this.transaction.completed &&
        !this.transaction.cancelled &&
        this.status != 'trialing' && !this.pausedUntil) {
        return true;
      }
      return false;
    },
    canUpdateSubscriptionItems() {
      if ((this.transaction.subscription || (this.product && this.product.type == 'service')) &&
        this.transaction.completed &&
        !this.transaction.cancelled && this.stripeAccountId) {
        return true;
      }
      return false
    },
    restartSubscription() {
      this.isRestarting = true;
      this.restartSubscriptionDialog = false;
      Api()
        .post(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/subscription/" +
          this.transactionId + "/restart"
        )
        .then((response) => {
          if (response.data) {
            //go to new transaction
            console.log(response.data)
            this.navigateTo({ name: "viewmember", params: { memberid: this.transaction.buyer._id } });
          }
        })
        .catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
          this.isRestarting = false;
          this.restartSubscriptionDialog = false;
          this.restartSubscriptionConfirm = false;
        });
    },
    refund() {
      if (!this.selectedInvoice) {
        return;
      }
      this.refunding = true;
      let data = {
        amount: this.refundAmount,
        transaction: this.transactionId,
      };
      Api()
        .patch(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/refund/" +
          this.selectedInvoice,
          data
        )
        .then((response) => {
          if (response.data) {
            this.showAlert("success", this.$t("message.success"));
            this.selectedInvoice = null;
            this.refundConfirmDialog = false;
            this.getTransactionData();
            this.refunding = false;
          }
        })
        .catch((err) => {
          this.showAlert("error", err.response.data.message);
        });
    },
    showRefundDialog(invoice) {
      if (invoice) {
        if (this.isInfinicept)
          this.selectedInvoice = invoice.id;
        else
          this.selectedInvoice = invoice.paymentIntent;
        this.refundAmount = invoice.amountPaid;
      } else {
        this.refundAmount = this.totalAmount * this.transaction.quantity
      }
      this.refundConfirmDialog = true;
    },
    retryPayment(invoice) {
      this.retryingPayment = true;
      Api()
        .post(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/subscription/" +
          this.transactionId + "/retry/payment",
          {
            paymentIntent: invoice.paymentIntent,
            invoice: invoice.id,
            index: this.invoices ? this.invoices.length + 1 : 1
          }
        )
        .then((response) => {
          if (response.data) {
            this.retryingPayment = false;
            this.getInvoices(true);
            this.getTransactionData();
            this.showAlert("success", this.$t("message.success"));
          }
        })
        .catch(() => {
          this.retryingPayment = false;
          this.showAlert("error", "Declined");
        });
    },
    updateTransactionNotes() {
      Api2()
        .patch(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/transactions/" +
          this.transactionId + "/notes", { notes: this.transaction.notes }
        )
        .then(() => {
          this.showAlert("success", this.$t("message.updated"));
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    updateTransactionDate() {
      Api2()
        .patch(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/transactions/" +
          this.transactionId + "/date", { date: this.createdDate }
        )
        .then(() => {
          this.showAlert("success", this.$t("message.updated"));
          this.transactionDateDialog = false;
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    updateAltMembers(v, action) {
      if (!v) {
        return;
      }
      let newAltMembers = null;
      if (action == 'remove') {
        newAltMembers = [];
        for (const am of this.transaction.altMembers) {
          if (am._id != v)
            newAltMembers.push(am._id);
        }
      } else {
        //add
        newAltMembers = [v];
        for (const am of this.transaction.altMembers) {
          newAltMembers.push(am._id);
        }
      }
      Api2()
        .patch(
          "payments/managedacademy/" +
          this.$store.state.managedAcademy +
          "/transactions/" +
          this.transactionId + "/altmembers",
          { altMembers: newAltMembers }
        )
        .then((response) => {
          this.transaction.altMembers = response.data.altMembers;
          this.newAltMember = null;
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    updateCouponData() {
      let coupon = null;
      this.updatingCoupon = true;
      if (this.discountedTotal > 0 && this.applyDiscount) {
        coupon = {
          discountPercent: this.discountPercent && this.discountPercent != "" ? new Number(this.discountPercent).valueOf() : null,
          discountAmount: this.discountAmount && this.discountAmount != "" ? new Number(this.discountAmount).valueOf() : null,
          duration: "forever",
          discountedTotal: this.discountedTotal,
          active: true,
        };
        if (this.couponDurationInMonths) {
          const expDate = new Date();
          expDate.setMonth(expDate.getMonth() + new Number(this.couponDurationInMonths).valueOf())
          this.couponExpirationDate = expDate;
          coupon.durationInMonths = new Number(this.couponDurationInMonths).valueOf();
          coupon.expirationDate = expDate;
        }
      }
      Api()
        .patch(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/transactions/" +
          this.transactionId + "/update/coupon",
          { coupon: coupon }
        )
        .then(() => {
          this.transaction.coupon = coupon;
          this.updatingCoupon = false;
          this.updateCouponDialog = false;
        })
        .catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
          this.updatingCoupon = false;
        });
    },
    openRenewDialog() {
      this.renewProductId = null;
      this.useDifferentProduct = false;
      this.renewCheckins = this.transaction.subscriptionCheckins || null;
      const baseDate = this.transaction.subscriptionEndDate
        ? new Date(this.transaction.subscriptionEndDate)
        : new Date();
      if (this.product && this.product.membershipMonths > 0) {
        baseDate.setMonth(baseDate.getMonth() + this.product.membershipMonths);
      } else if (this.product && this.product.membershipDays > 0) {
        baseDate.setDate(baseDate.getDate() + this.product.membershipDays);
      } else {
        baseDate.setMonth(baseDate.getMonth() + 1);
      }
      this.renewEndDate = baseDate;
      this.renewManualProducts = [];
      if (this.product) {
        this.renewManualProducts = [{ value: this.product._id, text: this.product.name, membershipCheckins: this.product.membershipCheckins }];
      }
      Api2()
        .get("admins/academy/" + this.$store.state.managedAcademy + "/products")
        .then((response) => {
          const data = response.data;
          if (data && data.goods) {
            this.renewManualProducts = data.goods
              .filter((p) => p.isMembership && p.active !== false)
              .map((p) => ({ value: p._id, text: p.name, membershipCheckins: p.membershipCheckins }));
          }
        })
        .catch(() => {});
      this.renewCashTransactionDialog = true;
    },
    renewSelectedProductCheckins() {
      if (this.renewProductId) {
        const p = this.renewManualProducts.find((p) => p.value === this.renewProductId);
        return p ? p.membershipCheckins : null;
      }
      return this.product ? this.product.membershipCheckins : null;
    },
    renewManualMembership() {
      let coupon = null;
      if (this.discountedTotal > 0 && this.applyDiscount) {
        coupon = {
          discountPercent: this.discountPercent && this.discountPercent != "" ? new Number(this.discountPercent).valueOf() : null,
          discountAmount: this.discountAmount && this.discountAmount != "" ? new Number(this.discountAmount).valueOf() : null,
          duration: "once",
          discountedTotal: this.discountedTotal,
          active: true,
        };
      }
      const payload = {
        subscriptionEndDate: this.renewEndDate,
        coupon: coupon,
        subscriptionCheckins: this.renewSelectedProductCheckins() && this.renewCheckins ? Number(this.renewCheckins) : null,
        productId: this.renewProductId || null,
      };
      Api()
        .patch(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/transactions/" +
          this.transactionId + "/renew",
          payload
        )
        .then(() => {
          this.checkMemberAndGo();
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    init() {
      const now = new Date();
      this.cancellationFeeRule = [this.validationRules.number];
      now.setDate(now.getDate() + 1);
      this.todayString = this.toNiceDateCustomFormatAdjusted(now, "YYYY-MM-DD");
      const minTrialStartDate = new Date();
      minTrialStartDate.setDate(minTrialStartDate.getDate() + 3);
      this.minTrialStartString = this.toNiceDateCustomFormatAdjusted(minTrialStartDate, "YYYY-MM-DD");
      if (this.sessionId && this.sessionStatus == "success") {
        //Separate complete with update
        if (this.redirectMode == 'purchase')
          this.completePurchase(this.sessionId)
        else
          this.completeUpdate(this.sessionId);
      } else if (this.sessionId && this.sessionStatus == "onboarding_success") {
        this.completePublicMembership(this.sessionId);
      }
      if (this.paymentIntentId) {
        Api2()
          .get(
            "payments/academy/" +
            this.$store.state.managedAcademy +
            "/payment/" +
            this.paymentIntentId
          )
          .then((response) => {
            this.transaction = response.data.transaction;
            if (!this.transaction.billingDate) {
              this.transaction.billingDate = this.transaction.time;
            }
            this.checkoutSession = response.data.session;
            if (this.transaction.email)
              this.email = this.transaction.email;
            else if (this.transaction.buyer)
              this.email = this.transaction.buyer.email;
            this.altEmail = this.email;
            this.loading = false;
            this.product = response.data.transaction.product;
            this.createdDate = response.data.transaction.time;
            this.invoices = null;
            this.currency = response.data.transaction.currency;
            if (this.transaction.paymentsModule == 'infinicept')
              this.isInfinicept = true;
            if (!this.transaction.allowedCustomClasses)
              this.transaction.allowedCustomClasses = [];
          });
      } else {
        Api2()
          .get("admins/academy/" + this.$store.state.selectedAcademy)
          .then((response) => {
            this.academy = response.data;
            this.academyImage = this.userImg(this.academy.image);
            this.getTransactionData();
          });
      }
    },
    updateBillingDate(cancel) {
      if ((this.updateBillingDateConfirm || cancel) && this.transaction.completed) {
        this.updateBillingDateConfirm = false;
        this.updatingBillingCycle = true;
        const billingDate = this.newBillingDate;
        billingDate.setHours(1);//To ensure we get all day to process if something goes wrong
        let payload = { billingDate: billingDate, skipProration: this.skipProration, isSameDate: this.isSameDate(new Date(), billingDate) };
        if (cancel)
          payload = { cancelUpdate: true };
        Api()
          .patch(
            "payments/academy/" +
            this.$store.state.managedAcademy +
            "/transactions/" +
            this.transactionId + "/billingdate",
            payload
          )
          .then(() => {
            this.transaction.billingDate = billingDate;
            this.billingDateDialog = false;
            this.showAlert("success", this.$t("message.updated"));
            this.getInvoices();
            this.updatingBillingCycle = false;
            if (cancel) {
              this.newBillingDate = new Date();
              this.transaction.updateBillingDate = null;
            }
          })
          .catch((e) => {
            this.updatingBillingCycle = false;
            if (e)
              this.showAlert("error", this.$t("message.error_generic"));
          });
      }
    },

    updateCheckinsDate() {
      Api()
        .patch(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/transactions/" +
          this.transactionId + "/subscription-checkins",
          { subscriptionCheckins: this.transaction.subscriptionCheckins }
        )
        .then(() => {
          this.editSubscriptionCheckinsDialog = false;
          this.showAlert("success", this.$t("message.updated"));
          this.getTransactionData();
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    resumePublicMembershipCheckout() {
      const data = {
        transaction: this.transactionId,
        academy: this.$store.state.selectedAcademy,
        product: this.product._id,
        email: this.transaction.email
      };
      Api()
        .post(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/resume/public/onboarding", data
        )
        .then((result) => {
          if (this.$store.state.stripeAccountId || stripeAccountId) {
            const stripePublishableKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY;
            const sessionId = result.data.sessionId;
            /*eslint-disable no-undef*/
            let stripe = Stripe(stripePublishableKey, {
              stripeAccount: this.stripeAccountId ? this.stripeAccountId : this.$store.state.stripeAccountId
            });
            /*eslint-disable no-undef*/
            stripe.redirectToCheckout({
              sessionId: sessionId
            });
          }
        });
    },
    completePublicMembership(s) {
      let sessionId = this.sessionId;
      if (s)
        sessionId = s;
      if (!sessionId)
        sessionId = this.transaction.sessionId;
      if (sessionId) {
        Api2()
          .get(
            "payments/academy/" +
            this.$store.state.managedAcademy +
            "/complete/public/onboarding?status=success&s=" + sessionId
          )
          .then(() => {
            console.log('Public transaction updated [session=' + sessionId + ']');
            this.getTransactionData();
          });
      } else {
        console.log('Cannot auto resume onboarding. ', this.transactionId)
      }
    },
    getTransactionData() {
      Api2()
        .get(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/payments/transactions/" +
          this.transactionId
        )
        .then((response) => {
          this.transaction = response.data.transaction;
          this.product = response.data.product;
          if (response.data.force_complete)
            this.pendingUpdateCompletion = response.data.force_complete;
          if (!this.transaction.billingDate) {
            this.transaction.billingDate = this.transaction.time;
          }
          this.receipt = this.transaction.receipt;
          this.customAchData = response.data.subscription;
          if (this.customAchData && !this.transaction.completed && this.customAchData.clientSecret) {
            const endDate = new Date(this.transaction.createdAt);
            endDate.setDate(endDate.getDate() + 1);
            this.isIncompleteCustomAch = endDate;
          }
          this.isMember = response.data.isMember;
          const onboarding = response.data.onboardingVisitor;
          if (onboarding && this.sessionStatus != "onboarding_success") {
            //If it was comoleetd already, lets go ahead and continue, else, allow completion from here
            if (response.data.subscription && response.data.subscription.status == 'active') {
              this.completePublicMembership(response.data.session.id)
            } else if (response.data.session && response.data.session.status == "paid") {
              this.completePublicMembership(response.data.session.id)
            } else {
              this.onboarding = onboarding;
            }
          }
          this.isExpiredTransaction();
          this.checkoutSession = response.data.session;
          if (this.checkoutSession && this.checkoutSession.automaticTax) {
            this.taxData = {
              enabled: true,
              totalDetails: this.checkoutSession.totalDetails
            };
          }
          if (this.transaction.coupon && this.transaction.coupon.discountedTotal > 0) {
            this.discountAmount = this.transaction.coupon.discountAmount;
            this.discountPercent = this.transaction.coupon.discountPercent;
            this.discountedTotal = this.transaction.coupon.discountedTotal;
            this.couponExpirationDate = this.transaction.coupon.expirationDate;
            this.couponDurationInMonths = this.transaction.coupon.durationInMonths;
            this.applyDiscount = true;
          }
          this.loading = false;
          if (this.transaction) {
            this.history = response.data.history;
            if (this.transaction.allowedClassCount && this.transaction.allowedClassCount.interval)
              this.allowedClassCount = this.transaction.allowedClassCount;
            if (!this.transaction.allowedCustomClasses)
              this.transaction.allowedCustomClasses = [];
            this.signedContract = response.data.signedContract;
            if (response.data.totalCheckins)
              this.totalCheckins = response.data.totalCheckins;
            if (this.transaction.expired)
              this.expired = true;
            else
              this.expired = false;
            if (!this.expired && response.data.expired)
              this.expired = response.data.expired;
            if (!this.sessionStatus && response.data.paymentMethod) {
              this.paymentMethod = response.data.paymentMethod;
              if (this.paymentMethod.expYear) {
                let expDate = new Date();
                expDate.setFullYear(this.paymentMethod.expYear);
                expDate.setMonth(this.paymentMethod.expMonth - 1);
                this.paymentMethod.expDate = expDate;
              }
              this.paymentMethod.expired = this.expiredPaymentMethod(this.paymentMethod);
            }
            if (this.transaction.recurrence) {
              this.totalAmount = this.transaction.recurrence.totalAmount;
            } else {
              this.totalAmount = this.transaction.amount * this.transaction.quantity
            }
            if (this.transaction.refunds && this.transaction.refunds.length > 0) {
              this.totalRefunded = 0;
              for (const t of this.transaction.refunds) {
                this.totalRefunded += t.amount;
              }
            }
            if (this.transaction.email)
              this.email = this.transaction.email;
            if (!this.email) {
              if (!this.transaction.buyer)
                this.email = response.data.session.customer_email;
              else this.email = this.transaction.buyer.email;
            }
            this.altEmail = this.email;
            //WE now display the product or products always as a list
            this.transactionItems = [];
            if (this.product) {
              this.transactionItems.push(
                {
                  product: this.product,
                  amountDecimal: this.product.amountDecimal,
                  quantity: 1,
                }
              );
            }
            if (this.transaction.items && this.transaction.items.length) {
              for (let i of this.transaction.items) {
                if (!this.product || !i.product || i.product._id.toString() != this.product._id.toString()) {
                  this.transactionItems.push({
                    product: i.product,
                    amountDecimal: i.amountDecimal,
                    quantity: i.quantity ? i.quantity : 1,
                    name: i.name,
                    description: i.description
                  });
                }
              }
            }
            if (this.canUpdateSubscriptionItems())
              this.loadAvailableSubscriptions();

            if (this.transaction.completed && !this.transaction.cancelled) {
              this.getPaymentTypes();
              if (this.product.type != 'service' && this.transaction)
                this.selectedInvoice = response.data.paymentIntent ? response.data.paymentIntent.id : null;
            }
            this.fillCancellationFees();
            if (this.product.type == 'service')
              this.isSubscription = true;
            if (this.product.type == 'service' || this.product.isMembership)
              this.isMembership = true;
            this.subscriptionEndDate = new Date(this.transaction.subscriptionEndDate);
            if (this.transaction.paidWithCash && this.transaction.subscriptionEndDate) {
              const d = new Date(this.transaction.subscriptionEndDate);
              if (this.product.membershipMonths > 0) {
                d.setMonth(d.getMonth() + this.product.membershipMonths);
              } else if (this.product.membershipDays > 0) {
                d.setDate(d.getDate() + this.product.membershipDays);
              } else {
                d.setMonth(d.getMonth() + 1);
              }
              this.renewEndDate = d;
            }
            if (this.product.amountDecimal > 0 && this.product.type == 'service' && this.transaction.buyer) {
              Api2()
                .get(
                  "payments/academy/" +
                  this.$store.state.managedAcademy +
                  "/customer/" +
                  this.transaction.buyer._id
                )
                .then((response) => {
                  if (response.data && response.data.paymentMethod) {
                    this.savedCard = response.data.paymentMethod;
                    this.savedCard.expired = this.expiredPaymentMethod(this.savedCard);
                  }
                });
            }
            if (response.data.transaction.pausedUntil && !response.data.transaction.cancelled) {
              const d = new Date();
              const d2 = new Date(response.data.transaction.pausedUntil);
              if (d2 < d) {
                this.resumeSubscription();
              } else {
                this.pausedUntil = response.data.transaction.pausedUntil;
              }
            }
            //Auto-expire credit checkout session due to inactivity?
            this.expirationTime = new Date(this.transaction.time);
            this.expirationTime.setHours(this.expirationTime.getHours() + 23);
            this.expirationTime.setMinutes(
              this.expirationTime.getMinutes() + 30
            );
            if (this.transaction.paymentsModule == 'infinicept')
              this.isInfinicept = true;
            /*
            const today = new Date();
            if (
              !this.transaction.cancelled &&
              !this.transaction.completed &&
              today > this.expirationTime &&
              !this.transaction.expired &&
              this.checkoutSession
            ) {
              //Process expiration
              this.transaction.cancelled = new Date();
              this.transaction.expired = true;
              console.log('HERE')
              if (
                this.transaction.subscription ||
                (this.transaction.product &&
                  this.transaction.product.type == "service")
              ) {
                console.log('We dont automatically cancel transactins anymore');
                //this.cancelSubscription(true);
              } else {
                //Dont cancel this
                //this.cancelOneTimePayment(true);
              }
            }
            */
          }
          //this.invoices = response.data.invoices;
          if (this.invoiceId) {
            this.getInvoices();
          }
          this.createdDate = response.data.transaction.time;
          this.currency = response.data.transaction.currency;
          if (this.isCustomAchTransaction(this.transaction) && !this.transaction.completed)
            this.status = 'pending_payment';
          else if (response.data.subscription)
            this.status = response.data.subscription.status;
          else if (response.data.session && (response.data.session.status == 'unpaid' || response.data.session.status == 'pending_payment'))
            this.status = response.data.session.status;
          else if (this.transaction.recurrence && this.transaction.recurrence.currentStatus)
            this.status = this.transaction.recurrence.currentStatus;

          if (this.transaction.subscriptionEndDate) this.isSubscription = true;
          if (this.transaction.subscriptionCheckins) this.isSubscription = true;
        });
    },
    getInvoices(force) {
      if (this.invoices.length == 0 || force) {
        this.loadingInvoices = true;
        Api2()
          .get(
            "payments/academy/" +
            this.$store.state.managedAcademy +
            "/transactions/" +
            this.transactionId + "/invoices"
          )
          .then((response) => {
            this.loadingInvoices = false;
            if (response.data.invoices) {
              this.nextBillingDate = response.data.upcomingInvoice ? response.data.upcomingInvoice : response.data.nextBillingDate;
              this.scheduledBillingDateUpdate = response.data.scheduledBillingDateUpdate ? response.data.scheduledBillingDateUpdate : null;
              this.invoices = response.data.invoices;
              for (const i of this.invoices) {
                if (i.id == this.invoiceId) {
                  this.targetInvoice = i;
                }
              }
            }
          });
      }
    },
    getPaymentTypes() {
      Api2()
        .get(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/paymenttypes"
        )
        .then((response) => {
          if (response.data && response.data.paymentMethods && response.data.paymentMethods.length == 2) {
            this.paymentTypes.push({ text: this.$t("message.credit_card"), value: 'card' });
            this.paymentTypes.push({ text: this.$t("message.ach"), value: response.data.paymentMethods[1] });
            this.selectedPaymentOptions.push('card');
          }
        });
    },
    isExpiredTransaction() {
      if (this.transaction && this.transaction.category == 'membership') {
        if (this.transaction.subscriptionStartDate) {
          const today = new Date();
          today.setHours(1);
          today.setMinutes(1);
          const startDate = new Date(this.transaction.subscriptionStartDate);
          startDate.setMinutes(59);
          startDate.setHours(23);
          if (today > startDate) {
            this.tooCloseToStartDate = true;
          } else {
            const daysBetween = Math.abs(this.daysBetween(today, startDate));
            if (daysBetween <= 2) {
              this.tooCloseToStartDate = true;
            }
          }
        }
      }
    },
    updateTrialEndDate() {
      if (this.trialPeriodDate) {
        this.trialPeriodDate.setHours(1);
        Api()
          .patch(
            "payments/academy/" +
            this.$store.state.managedAcademy +
            "/transactions/" +
            this.transactionId + "/trialenddate", { trialPeriodEndDate: this.trialPeriodDate }
          )
          .then((response) => {
            if (response.data && response.data.updated) {
              this.getTransactionData();
              this.showAlert("success", this.$t("message.updated"));
              this.transaction.subscriptionStartDate = response.data.newDate;
              this.editTrialEndDateDialog = false;
            }
          })
          .catch((e) => {
            if (e)
              this.showAlert("error", this.$t("message.error_generic"));
          });
      } else {
        this.showAlert("error", this.$t("message.fill_all_params"));
      }
    },
    fillCancellationFees() {
      //create up to 3 months of cancellation fees;
      const price = this.product.amountDecimal;
      if (this.paymentMethod && this.paymentMethod.type == 'card') {
        let fees = [];
        fees.push({ name: '1 ' + this.$t("message.month") + ' - ' + this.academy.currencySymbol + price, value: 1 });
        fees.push({ name: '2 ' + this.$t("message.months") + ' - ' + this.academy.currencySymbol + price * 2, value: 2 });
        fees.push({ name: '3 ' + this.$t("message.months") + ' - ' + this.academy.currencySymbol + price * 3, value: 3 });
        fees.push({ name: this.$t("message.custom"), value: 4 });
        this.cancellationFees = fees;
      }
    },
    goToSignedDocument() {
      if (this.signedContract) {
        window.open("/secure/printwaiver/" + this.signedContract._id, "_blank");
      }
    },
    leave() {
      if (this.sessionId)
        this.navigateTo({ name: 'transactions' });
      else
        this.goBack();
    },
    getInvoiceDate(invoice) {
      //.created)
      if (invoice && invoice.created) {
        if (this.isInfinicept)
          return new Date(invoice.created);
        else
          return new Date(invoice.created * 1000);
      }
    },
    checkMemberAndGo() {
      if (this.transaction.buyer) {
        this.goToMember(this.transaction.buyer._id, this.transaction.buyer.displayName);
      }
    },
    removeCashTransaction() {
      Api()
        .delete(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/transactions/cash/" +
          this.transactionId
        )
        .then((response) => {
          if (response) this.navigateTo({ name: "transactions" });
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    getStatusExplained(status) {
      return this.$t("message.invoice_status_" + status);
    },
    resumeSubscription() {
      Api()
        .patch(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/transactions/" +
          this.transactionId +
          "/resume"
        )
        .then((response) => {
          if (response) {
            this.resumeSubscriptionDialog = false;
            this.pausedUntil = null;
            this.transaction.pausedUntil = null;
            this.transaction.pauseDate = null;
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", "Error auto-resuming");
        });
    },
    pauseSubscription() {
      if (this.subscriptionResumeDate) {
        this.pausingSubscription = true;
        let subscriptionResumeDate = new Date(this.subscriptionResumeDate);
        subscriptionResumeDate.setHours(1);
        let patchData = {
          resumesAt: subscriptionResumeDate,
          sendMembershipPausedEmail: this.sendMembershipPausedEmail,
        };
        if (this.pauseDate && this.schedulePause)
          patchData.pausesAt = this.pauseDate;
        Api()
          .patch(
            "payments/academy/" +
            this.$store.state.managedAcademy +
            "/transactions/" +
            this.transactionId +
            "/pause",
            patchData
          )
          .then((response) => {
            if (response) {
              this.pauseSubscriptionDialog = false;
              this.pausedUntil = subscriptionResumeDate;
              this.transaction.pausedUntil = subscriptionResumeDate;
              this.transaction.pauseDate = this.pauseDate;
              this.showAlert("success", this.$t("message.success"));
              this.sendMembershipPausedEmail = false;
              this.pausingSubscription = false;
            }
          })
          .catch((e) => {
            this.pausingSubscription = false;
            this.sendMembershipPausedEmail = false;
            console.log(e)
            if (e)
              this.showAlert(
                "error",
                this.$t("message.error_generic")
              );
          });
      } else {
        this.pausingSubscription = false;
        this.sendMembershipPausedEmail = false;
        this.showAlert("error", this.$t("message.fill_all_params"));
      }
    },
    removeSubscriptionEndDate(val) {
      if (!val) {
        Api()
          .patch(
            "payments/academy/" +
            this.$store.state.managedAcademy +
            "/transactions/" +
            this.transactionId +
            "/remove/membership"
          )
          .then((response) => {
            if (response) {
              this.isSubscription = false;
              this.subscriptionEndDate = null;
              this.subscriptionCheckins = null;
              this.subscriptionEndDateDialog = false;
              this.transaction.subscriptionEndDate = null;
              this.showAlert("success", this.$t("message.success"));
            }
          });
      }
    },
    addSubscriptionEndDate() {
      if (this.subscriptionEndDate) {
        this.subscriptionEndDate = this.toEndOfDay(this.subscriptionEndDate);
        Api()
          .patch(
            "payments/academy/" +
            this.$store.state.managedAcademy +
            "/transactions/" +
            this.transactionId +
            "/subcriptionenddate",
            { subscriptionEndDate: this.subscriptionEndDate, now: new Date() }
          )
          .then((response) => {
            if (response) {
              this.isSubscription = true;
              this.transaction.subscriptionEndDate = this.subscriptionEndDate;
              this.subscriptionEndDateDialog = false;
              this.showAlert("success", this.$t("message.success"));
              this.editSubscriptionEndDate = false;
              this.getTransactionData();
            }
          })
          .catch((e) => {
            if (e)
              this.showAlert("error", this.$t("message.error_generic"));
          });
      }
    },
    getInvoiceColor(i) {
      if (i.paid) {
        if (i.attempts > 0 && i.amountPaid > 0) return this.themeColor('success');
        else return this.themeColor('success');
      } else {
        return this.themeColor('error');
      }
    },
    completeUpdate(id) {
      this.updating = true;
      this.processingPayment = true;
      Api()
        .patch(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/payments/cc/update/complete/" +
          this.transactionId +
          "/" +
          id
        )
        .then((response) => {
          if (response) {
            //Load payment method again
            Api2()
              .get(
                "admins/academy/" +
                this.$store.state.managedAcademy +
                "/payments/transactions/" +
                this.transactionId
              )
              .then((response) => {
                if (response.data.paymentMethod) {
                  this.paymentMethod = response.data.paymentMethod;
                  let expDate = new Date();
                  expDate.setFullYear(this.paymentMethod.expYear);
                  expDate.setMonth(this.paymentMethod.expMonth - 1);
                  this.paymentMethod.expDate = expDate;
                }
                if (response.data.subsription) {
                  this.transaction.subsription = response.data.subsription;
                }
                this.showAlert("success", this.$t("message.updated"));
                if (this.transaction && !this.transaction.completed)
                  this.transaction.completed = new Date();
                this.updating = false;
                this.getInvoices();
                this.isSubscription = true;
                this.pendingUpdateCompletion = null;
                this.processingPayment = false;
              });
          }
        }).catch(() => {
          this.handlingCharges = false;
          this.showAlert("error", this.$t("message.error_generic"));
          this.pendingUpdateCompletion = false;
        });
    },
    updateCreditCard() {
      this.navigateTo({
        name: "updatecard",
        params: { transactionId: this.transactionId },
        query: { saveCard: this.saveCard, paymentOptions: this.selectedPaymentOptions },
      });
    },
    sendUpdateNotification() {
      this.handlingCharges = true;
      let postData = { sendNotification: true };
      if (this.saveCreditCard) postData.saveCreditCard = true;
      if (this.selectedPaymentOptions && this.selectedPaymentOptions.length >= 1)
        postData.paymentTypes = this.selectedPaymentOptions;
      let postUrl =
        "admins/academy/" +
        this.$store.state.managedAcademy +
        "/payments/cc/update/" + this.transactionId;
      Api()
        .patch(postUrl, postData)
        .then((response) => {
          const data = response.data;
          if (data) {
            this.handlingCharges = false;
            this.updateCardDialog = false;
            this.showAlert(
              "success",
              this.$t("message.success"),
            );
          }
        })
        .catch((e) => {
          this.handlingCharges = false;
          if (e) {
            if (e.response.data && e.response.data.message)
              this.showAlert("error", e.response.data.message);
            else
              this.showAlert("error", this.$t("message.error_generic"));
          }
        });
    },
    cancelOneTimePayment(expired) {
      Api()
        .delete(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/transactions/" +
          this.transactionId +
          (expired ? "?expired=true" : "")
        )
        .then((response) => {
          if (response) {
            this.transaction.cancelled = new Date();
            this.init();
            this.showAlert("success", this.$t("message.cancelled"));
          }
        })
        .catch((e) => {
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    processScheduleCancellation(clear) {
      this.processingCancellation = true;
      let data = null;
      if (clear) {
        data = { clear: true };
      } else {
        const cancellationDate = new Date(this.cancellationDate);
        cancellationDate.setHours(1);
        cancellationDate.setMinutes(0);
        data = {
          cancellationDate: cancellationDate,
          cancelAndRemoveMember: this.cancelAndRemoveMember
        };
      }
      if (data) {
        Api()
          .patch(
            "admins/academy/" +
            this.$store.state.managedAcademy +
            "/payments/subscription/cancel/" +
            this.transactionId + "/schedule", data
          )
          .then((response) => {
            if (response) {
              this.showAlert("success", this.$t("message.success"));
              this.processingCancellation = false;
              this.cancelSubscriptionDialog = false;
              if (clear) {
                this.transaction.cancellationDate = null;
              } else {
                this.transaction.cancellationDate = data.cancellationDate;
                this.transaction.cancelAndRemoveMember = data.cancelAndRemoveMember;
              }
              this.scheduleCancellation = false;
              this.cancelAndRemoveMember = false;
            }
          })
          .catch(() => {
            this.processingCancellation = false;
            this.showAlert("error", this.$t("message.error_generic"));
          });
      }
    },
    cancelSubscription(expired, skipEmail) {
      let customFee = null;
      this.processingCancellation = true;
      if (this.cancellationFee == "4" && this.customCancellationFee > 0) {
        customFee = "&custom_fee=" + this.customCancellationFee;
      }
      Api()
        .delete(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/payments/subscription/cancel/" +
          this.transactionId + "?" +
          (expired ? "&expired=true" : "") +
          (skipEmail ? "&skipemail=true" : "") +
          (this.cancellationFee && this.cancellationFee > 0 ? "&fee=" + this.cancellationFee : "") +
          (customFee ? customFee : "")
        )
        .then((response) => {
          if (response) {
            this.showAlert("success", this.$t("message.cancelled"));
            this.cancelSubscriptionDialog = false;
            this.cancelSubscriptionConfirm = false;
            this.transaction.cancelled = new Date();
            this.status = 'canceled';
            this.processingCancellation = false;
          }
        })
        .catch((e) => {
          this.processingCancellation = false;
          if (e && e.response.data.message_translation)
            this.showAlert("error", this.$t("message." + e.response.data.message_translation));
          else if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    getPaymentStatus() {
      if (!this.transaction.cancelled && this.status) {
        return this.getSubscriptionStatusText(this.status);
      } else if (this.transaction.cancelled) {
        //Show what comes from Sgtripe
        if (this.transaction.subscription) return this.$t("message.inactive");
        else return this.$t("message.cancelled");
      } else if (this.transaction.completed) {
        if (this.transaction.subscription && this.status) {
          return this.$t("message.subscription_status_" + this.status);
        } else return this.$t("message.success");
      } else {
        return this.$t("message.waiting_for_payment");
      }
    },
    emailReceipt() {
      let emailData = {};
      let invoiceId = null;
      if (this.transaction.paymentsModule == 'easypay')
        emailData = { invoice: this.selectedInvoice };
      else
        invoiceId = this.selectedInvoice;
      emailData.email = this.altEmail;
      Api2()
        .post(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/payments/transactions/" +
          this.transaction._id + "/email/receipt/" +
          "?" + (invoiceId ? "&invoice=" + invoiceId : ""), emailData
        )
        .then((response) => {
          if (response) {
            this.sendEmailDialog = false;
            this.selectedInvoice = null;
            this.showAlert("info", this.$t("message.email_sent"));
          }
        });
    },
    printReceipt() {
      var p = this.$refs.print_div;
      PHE.printHtml(p.innerHTML);
    },
    isPending() {
      const s = this.transaction;
      if (s.payment_intent) {
        if (s.payment_intent.status == "requires_payment_method") return true;
      } else return false;
    },
    isDesktopTransaction() {
      const t = this.transaction;
      if (t.processedBy == "desk") return true;
      return false;
    },
    canCompletePayment(inCash) {
      const t = this.transaction;
      if (inCash && this.product.type == 'service') {
        return false;
      }
      if (this.status == 'active') {
        return false;
      }
      if (t.processedBy == "desk" || t.processedBy == "user") {
        if (!t.completed &&
          !t.cancelled && !this.tooCloseToStartDate)
          return true;
      }
      return false;
    }
  },
};
</script>

<style scoped>
.card-size {
  max-width: 365px
}
</style>
